// Estado de los sensores: qué llega de cada equipo, cada cuánto, cuándo se calla y si lo que mide tiene sentido.
// Datos: /diagnostico (todos los equipos) y /diagnostico/:device (ficha), leídos del archivo permanente del cloud.
import React, { useState, useMemo, useRef, useId } from "react";
import {
  ComposedChart, BarChart, Bar, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, ReferenceArea,
} from "recharts";
import {
  Zap, Thermometer, Droplets, Gauge, Power, Cpu, CircleHelp, ArrowLeft, RefreshCw, Download, ChevronRight,
  BarChart3, CloudSun, Euro, Bell, CircleCheck, CircleX, TriangleAlert, Info, Minus, ArrowDown, ArrowUp, Smile, Meh, Frown,
  Snowflake, Flame, Moon, Droplet, ChevronLeft,
} from "lucide-react";
import api from "./services/api";
import useApi from "./hooks/useApi";
import {
  V, mezcla, colorTemp, estadoTemp, Casa3D, Termometro3D, Gota, Deposito3D, Cilindros, Bombilla, DibujoAparato, Reloj24, CalendarioColor, EscalaColor,
} from "./VisualesSensores";

const C = {
  ink: "#1F271D", ink2: "#3F473B", ink3: "#5C6457", ink4: "#8F968A", paper: "#F7F7F7", line: "#DDE2D7", soft: "#EEF0EB",
  primary: "#406020", moss: "#6F8F4E", light: "#EBF2E4", ochre: "#C08B2C", clay: "#B4532F", sky: "#3D6E8E",
};
const TONE = { good: C.moss, warn: C.ochre, bad: C.clay, info: C.sky, off: C.ink4 };
const TONE_TXT = { good: "Correcto", warn: "Revisar", bad: "Fallo", off: "Sin datos" };
const NIVEL_TXT = { bad: "Importante", warn: "Revisar", info: "Info" };
const SERIE_COL = [C.primary, C.ochre, C.sky, C.clay, "#8A6FB0", "#4E9A8F"];
const FASE_COL = { a: "#6F8F4E", b: "#C08B2C", c: "#3D6E8E", n: "#7D8478" };
const HORA = 3600000, DIA = 86400000;
const mono = { fontFamily: "var(--font-mono)" };
const card = { background: "#fff", border: `1px solid ${C.line}`, borderRadius: 16 };
const pill = (on) => ({
  padding: "6px 14px", borderRadius: 999, border: `1px solid ${on ? C.primary : C.line}`, background: on ? C.primary : "#fff",
  color: on ? "#fff" : C.ink, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font-sans)", whiteSpace: "nowrap",
});
const btn = {
  display: "inline-flex", alignItems: "center", gap: 7, padding: "7px 14px", borderRadius: 999, border: `1px solid ${C.line}`,
  background: "#fff", color: C.ink, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font-sans)",
};
const input = { padding: "6px 10px", border: `1px solid ${C.line}`, borderRadius: 10, fontSize: 13, fontFamily: "var(--font-sans)", color: C.ink, background: "#fff" };

const TIPO = {
  energia_trifasica: { label: "Energía trifásica", icon: Zap, grupo: "Energía" },
  energia_monofasica: { label: "Energía monofásica", icon: Zap, grupo: "Energía" },
  clima: { label: "Temperatura y humedad", icon: Thermometer, grupo: "Clima" },
  pulsos_agua: { label: "Contador de agua", icon: Droplets, grupo: "Agua" },
  nivel_agua: { label: "Nivel de depósito", icon: Gauge, grupo: "Agua" },
  actuador: { label: "Actuador", icon: Power, grupo: "Actuadores" },
  pasarela: { label: "Pasarela", icon: Cpu, grupo: "Pasarelas" },
  desconocido: { label: "Sin clasificar", icon: CircleHelp, grupo: "Otros" },
};
const GRUPOS = ["Pasarelas", "Energía", "Clima", "Agua", "Actuadores", "Otros"];

// ── Nombres y unidades de cada medida ───────────────────────────────────────
const CAMPO = {
  power: ["Potencia", "W"], voltage: ["Tensión", "V"], current: ["Corriente", "A"], power_factor: ["Factor de potencia", ""],
  ac_frequency: ["Frecuencia", "Hz"], energy: ["Energía acumulada", "kWh"], energy_produced: ["Energía vertida", "kWh"],
  produced_energy: ["Energía vertida", "kWh"], consumption: ["Consumo acumulado", "kWh"], temperature: ["Temperatura", "°C"],
  humidity: ["Humedad", "%"], battery: ["Batería", "%"], linkquality: ["Señal Zigbee", "LQI"], device_temperature: ["Temperatura interna", "°C"],
  liquid_level_percent: ["Nivel del depósito", "%"], liquid_depth: ["Altura del agua", "m"], liquid_depth_max: ["Altura máxima del agua", "m"],
  installation_height: ["Altura de instalación", "m"], max_set: ["Aviso de depósito lleno", "%"], mini_set: ["Aviso de depósito vacío", "%"],
  pulses: ["Pulsos", ""], power_outage_count: ["Cortes de luz (contador)", ""], overload_protection: ["Protección de sobrecarga", "W"],
  brightness: ["Brillo", ""], color_temp: ["Temperatura de color", ""], modbus_ok: ["Medidor responde", "%"], lecturas: ["Lecturas del medidor", ""],
  consumer_connected: ["Algo enchufado", "%"], auto_off: ["Apagado automático", ""], led_disabled_night: ["LED apagado de noche", ""],
  power_outage_memory: ["Recuerda el estado tras un corte", ""], comfort_temperature_max: ["Confort: temperatura máxima", "°C"],
  comfort_temperature_min: ["Confort: temperatura mínima", "°C"], comfort_humidity_max: ["Confort: humedad máxima", "%"],
  comfort_humidity_min: ["Confort: humedad mínima", "%"], update_frequency: ["Intervalo de envío configurado", "s"],
  data_report_duration: ["Periodo de informe configurado", "s"], divisor: ["Divisor", ""], multiplier: ["Multiplicador", ""],
  // textos
  temperature_units: ["Unidad de temperatura", ""], unitOfMeasure: ["Unidad del contador", ""], source: ["Origen de la lectura", ""],
  edge: ["Pasarela", ""], medidor: ["Medidor", ""], medidor_ip: ["IP del medidor", ""], error: ["Último error", ""], estado: ["Estado", ""],
  state: ["Estado", ""], color_mode: ["Modo de color", ""], button_lock: ["Bloqueo del botón", ""], liquid_state: ["Estado del depósito", ""],
};
const FASE = { a: "fase A", b: "fase B", c: "fase C", n: "neutro" };
function etiqueta(campo, tipo) {
  if (tipo === "pulsos_agua") {
    if (campo === "energy") return ["Agua acumulada", "L"];
    if (campo === "voltage") return ["Tensión de la pila", "mV"];
    if (campo === "temperature") return ["Temperatura del módulo", "°C"];
  }
  const m = campo.match(/^(power|voltage|current|energy|energy_produced|power_factor)_([abcn])$/);
  if (m) { const [l, u] = etiqueta(m[1], tipo); return [`${l} ${FASE[m[2]]}`, u]; }
  const m2 = campo.match(/^voltage_(ab|bc|ca)$/);
  if (m2) return [`Tensión ${m2[1][0].toUpperCase()}-${m2[1][1].toUpperCase()}`, "V"];
  return CAMPO[campo] || [campo.replace(/_/g, " "), ""];
}
// Contadores: lo que se muestra es cuánto suben
function etiquetaInc(campo, tipo) {
  if (tipo === "pulsos_agua" && campo === "energy") return ["Agua consumida", "L"];
  if (/produced/.test(campo)) return ["Energía vertida", "kWh"];
  if (/^energy(_[abc])?$/.test(campo)) return [`Energía consumida${campo.length > 6 ? " " + FASE[campo.slice(-1)] : ""}`, "kWh"];
  return { consumption: ["Consumo", "kWh"], pulses: ["Pulsos", ""], power_outage_count: ["Cortes de luz", ""], lecturas: ["Lecturas del medidor", ""] }[campo] || [campo, ""];
}
const colorDe = (campo, i) => { const m = campo.match(/_([abcn])$/); return m && FASE_COL[m[1]] ? FASE_COL[m[1]] : SERIE_COL[i % SERIE_COL.length]; };
function referencias(campo, tipo) {
  if (/^voltage(_[abc])?$/.test(campo) && String(tipo).startsWith("energia")) return [[207, "207 V"], [253, "253 V"]];
  if (campo === "ac_frequency") return [[49.8, "49,8"], [50.2, "50,2"]];
  if (campo === "battery") return [[20, "batería baja"]];
  if (campo === "humidity") return [[85, "85 %"]];
  return [];
}

// ── Formato ─────────────────────────────────────────────────────────────────
const pad = (n) => String(n).padStart(2, "0");
const DSEM = ["dom", "lun", "mar", "mié", "jue", "vie", "sáb"];
const esNum = (v) => v !== null && v !== undefined && Number.isFinite(Number(v));
const nf = (v, d = 1) => (!esNum(v) ? "—" : Number(v).toLocaleString("es-ES", { maximumFractionDigits: Math.abs(v) >= 1000 ? 0 : d }));
const decimales = (u) => (["kWh", "Hz", "m", "A"].includes(u) ? 2 : 1);
const fmt = (v, u) => (!esNum(v) ? "—" : `${nf(v, decimales(u))}${u ? " " + u : ""}`);
const fechaHora = (t) => { const d = new Date(t); return `${pad(d.getDate())}/${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`; };
const diaCorto = (s) => { const d = new Date(s + "T12:00:00"); return `${DSEM[d.getDay()]} ${d.getDate()}/${d.getMonth() + 1}`; };
const durMin = (m) => (!esNum(m) ? "—" : m < 1 ? "menos de 1 min" : m < 60 ? `${Math.round(m)} min` : m < 2880 ? `${Math.round(m / 60)} h` : `${Math.round(m / 1440)} días`);
const hace = (m) => (!esNum(m) ? "nunca" : m < 1 ? "ahora mismo" : `hace ${durMin(m)}`);
const cada = (s) => (!esNum(s) || s <= 0 ? "—" : s < 90 ? `cada ${Math.round(s)} s` : s < 5400 ? `cada ${Math.round(s / 60)} min` : `cada ${nf(s / 3600, 1)} h`);
const hoyStr = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
const verde = (x) => `rgba(64, 96, 32, ${0.22 + 0.78 * Math.min(1, x)})`;

function toneEq(e) {
  if (e.estado === "caido") return "bad";
  if (e.estado === "sin_datos") return "off";
  if (e.avisos.some((a) => a.nivel === "bad")) return "bad";
  if (e.avisos.some((a) => a.nivel === "warn")) return "warn";
  return "good";
}
// Frecuencia: la del archivo; si es una pasarela que solo manda latidos, la de la memoria del servidor
function frecuencia(e) {
  if (esNum(e.intervalo_s)) return e.intervalo_s;
  const m = e.memoria;
  if (m && m.recibidos > 1) return (Date.now() - Date.parse(m.desde)) / 1000 / (m.recibidos - 1);
  return null;
}
function valorActual(e) {
  const t = e.textos || {};
  if (e.tipo === "pasarela") {
    if (t.edge === "zigbee2mqtt" || e.device === "zigbee2mqtt") return t.estado === "online" ? "conectado" : t.estado || "—";
    if (e.principal && e.principal.campo === "modbus_ok" && esNum(e.principal.ultimo)) return e.principal.ultimo ? "medidor responde" : "medidor sin respuesta";
    return "—";
  }
  const estado = t.state === "ON" ? "encendido" : t.state === "OFF" ? "apagado" : t.state;
  const p = e.principal;
  if (p && p.campo === "brightness" && esNum(p.ultimo)) return `${estado ? estado + " · " : ""}brillo ${Math.round((p.ultimo / 254) * 100)} %`;
  if (!p || !esNum(p.ultimo)) return estado || "—";
  const [, u] = etiqueta(p.campo, e.tipo);
  let s = p.contador && e.tipo === "pulsos_agua" ? `${nf(p.ultimo, 0)} ${u}` : fmt(p.ultimo, u);
  if (e.tipo === "clima") { const h = (e.campos || []).find((x) => x.campo === "humidity"); if (h) s += ` · ${nf(h.ultimo, 0)} %`; }
  if (estado && e.actuador) s += ` · ${estado}`;
  return s;
}

// ── Piezas pequeñas ─────────────────────────────────────────────────────────
function Mark({ tone, size = 9 }) {
  return <span aria-hidden style={{ width: size, height: size, borderRadius: 1.5, flexShrink: 0, display: "inline-block", background: TONE[tone] || TONE.off }} />;
}
function Eyebrow({ children, style }) {
  return <div style={{ ...mono, fontSize: 10.5, letterSpacing: 1.1, textTransform: "uppercase", color: C.ink3, ...style }}>{children}</div>;
}
function Tarjeta({ titulo, extra, children, style }) {
  return (
    <div style={{ ...card, padding: 18, ...style }}>
      {(titulo || extra) && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
          {titulo && <Eyebrow>{titulo}</Eyebrow>}
          {extra}
        </div>
      )}
      {children}
    </div>
  );
}
function Cifra({ label, valor, sub, color }) {
  return (
    <div style={{ ...card, padding: "14px 16px", position: "relative", overflow: "hidden", minWidth: 0 }}>
      <Eyebrow>{label}</Eyebrow>
      <div style={{ fontSize: 26, fontWeight: 700, color: C.ink, marginTop: 8, fontFamily: "var(--font-serif)", lineHeight: 1.1, overflowWrap: "anywhere" }}>{valor}</div>
      {sub && <div style={{ fontSize: 12.5, color: C.ink3, marginTop: 4 }}>{sub}</div>}
      <div aria-hidden style={{ position: "absolute", left: 18, right: 18, bottom: 0, height: 2, background: color || C.primary, opacity: 0.8 }} />
    </div>
  );
}
function MiniBarras({ valores, dias, alto = 24 }) {
  const max = Math.max(1, ...valores);
  const muchas = valores.length > 14;
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: muchas ? 1 : 2, height: alto }}>
      {valores.map((v, i) => (
        <div key={i} title={`${dias[i] ? diaCorto(dias[i]) : ""}: ${v} mensajes`}
          style={{ width: muchas ? 3 : 7, height: v ? Math.max(2, Math.round((alto * v) / max)) : 2, background: v ? (i === valores.length - 1 ? C.primary : C.moss) : C.soft, borderRadius: 1 }} />
      ))}
    </div>
  );
}
function Actividad48({ horas, limite }) {
  const base = Math.floor(Date.now() / HORA) * HORA - 47 * HORA;
  const max = Math.max(1, ...horas);
  return (
    <div style={{ display: "flex", gap: 1, alignItems: "center" }}>
      {horas.map((n, i) => (
        <div key={i} title={`${fechaHora(base + i * HORA)} · ${n} mensaje${n === 1 ? "" : "s"}`}
          style={{ width: 3, height: 16, borderRadius: 1, background: n ? verde(n / max) : limite <= 60 ? "#EBC9BA" : C.soft }} />
      ))}
    </div>
  );
}
function Bateria({ b }) {
  if (!b || !esNum(b.ultima)) return <span style={{ color: C.ink4 }}>—</span>;
  const col = b.ultima <= 15 ? C.clay : b.ultima <= 30 ? C.ochre : C.ink2;
  return <span style={{ color: col, fontWeight: b.ultima <= 30 ? 700 : 500 }} title={b.dias_restantes ? `A este ritmo dura unos ${b.dias_restantes} días` : undefined}>{nf(b.ultima, 0)} %</span>;
}
function Disp({ v }) {
  if (!esNum(v)) return <span style={{ color: C.ink4 }} title="Sin historial suficiente">—</span>;
  const col = v >= 95 ? C.moss : v >= 80 ? C.ochre : C.clay;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span style={{ width: 34, height: 4, background: C.soft, borderRadius: 2, overflow: "hidden", display: "inline-block" }}>
        <span style={{ display: "block", width: `${Math.max(2, v)}%`, height: "100%", background: col }} />
      </span>
      <span style={{ color: col, fontWeight: 600 }}>{nf(v, v >= 99.95 || v < 10 ? 0 : 1)} %</span>
    </span>
  );
}
function Avisos({ lista, nombreDe, onOpen, max }) {
  const [todos, setTodos] = useState(false);
  const vis = todos || !max ? lista : lista.slice(0, max);
  return (
    <div>
      {vis.map((a, i) => (
        <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "10px 0", borderTop: i ? `1px solid ${C.line}` : "none" }}>
          <Mark tone={a.nivel} size={10} />
          <div style={{ flex: 1, minWidth: 0, fontSize: 14, color: C.ink2, lineHeight: 1.5, marginTop: -3 }}>
            {a.device && onOpen && (
              <button onClick={() => onOpen(a.device)} style={{ border: "none", background: "none", padding: 0, cursor: "pointer", fontWeight: 700, color: C.ink, fontSize: 14, fontFamily: "var(--font-sans)" }}>
                {nombreDe(a.device)}
              </button>
            )}
            {a.device && onOpen ? " · " : ""}{a.texto}
          </div>
          <span style={{ ...mono, fontSize: 10, letterSpacing: 0.8, textTransform: "uppercase", color: TONE[a.nivel] }}>{NIVEL_TXT[a.nivel]}</span>
        </div>
      ))}
      {max && lista.length > max && (
        <button style={{ ...btn, marginTop: 8 }} onClick={() => setTodos(!todos)}>{todos ? "Ver menos" : `Ver los ${lista.length} avisos`}</button>
      )}
    </div>
  );
}

// ── Página ──────────────────────────────────────────────────────────────────
export default function DiagnosticoPage({ acc, isMobile }) {
  const [dias, setDias] = useState(7);
  const [sel, setSel] = useState(null);
  const [vista, setVista] = useState(() => { try { return localStorage.getItem("er_estado_vista") || "graficas"; } catch (e) { return "graficas"; } });
  const cambiarVista = (v) => { setVista(v); try { localStorage.setItem("er_estado_vista", v); } catch (e) { /* sin almacenamiento */ } };
  const top = useRef(null);
  const { data, loading, error, refetch } = useApi(() => api.getDiagnostico(acc.locationID, dias), [acc.locationID, dias], { pollMs: 60000 });
  const { data: inst } = useApi(() => api.getInstall(acc.locationID), [acc.locationID]);
  const info = useMemo(() => {
    const m = {};
    ((inst && inst.devices) || []).forEach((d) => { m[d.device] = d; });
    return m;
  }, [inst]);
  const nombreDe = (dev) => (info[dev] && info[dev].alias) || dev;
  const abrir = (dev) => { setSel(dev); setTimeout(() => top.current && top.current.scrollIntoView({ block: "start" }), 0); };

  const plan = String(acc.plan_contratado || acc.plan || "").toLowerCase();
  const d = data && data.locationID === acc.locationID ? data : null;
  const eqSel = d && sel ? d.equipos.find((e) => e.device === sel) : null;

  return (
    <div ref={top} style={{ padding: isMobile ? 16 : 32, fontFamily: "var(--font-sans)" }}>
      {eqSel ? (
        <Ficha key={eqSel.device} acc={acc} eq={eqSel} info={info[eqSel.device]} todos={d.equipos} nombreDe={nombreDe}
          onBack={() => setSel(null)} onOpen={abrir} isMobile={isMobile} />
      ) : (
        <>
          <div style={{ marginBottom: 18 }}>
            <h2 style={{ fontSize: isMobile ? 28 : 34, fontWeight: 700, color: C.primary, margin: "0 0 6px", fontFamily: "var(--font-serif)", lineHeight: 1.15 }}>Estado de los sensores</h2>
            <p style={{ color: C.ink3, fontSize: 15, margin: 0 }}>
              {acc.nombre || acc.locationID} · {vista === "graficas"
                ? "cómo van la luz, la temperatura, el agua y los aparatos del alojamiento."
                : "qué llega de cada equipo, cada cuánto y si lo que mide tiene sentido."}
            </p>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 20, borderBottom: `1px solid ${C.line}` }}>
            <div style={{ display: "flex", gap: 4 }}>
              {[["graficas", "Gráficas"], ["equipos", "Equipos y avisos"]].map(([v, l]) => (
                <button key={v} onClick={() => cambiarVista(v)}
                  style={{ border: "none", background: "none", cursor: "pointer", padding: "10px 14px", fontSize: 15, fontWeight: 700, fontFamily: "var(--font-sans)",
                    color: vista === v ? C.primary : C.ink3, borderBottom: `3px solid ${vista === v ? C.primary : "transparent"}`, marginBottom: -1 }}>
                  {l}
                  {v === "equipos" && d && d.resumen.avisos.bad + d.resumen.avisos.warn > 0 && (
                    <span style={{ marginLeft: 8, fontSize: 11.5, padding: "2px 8px", borderRadius: 999, background: d.resumen.avisos.bad ? "#F6E3DB" : "#F5EBD3", color: d.resumen.avisos.bad ? C.clay : "#7A5A12" }}>
                      {d.resumen.avisos.bad + d.resumen.avisos.warn}
                    </span>
                  )}
                </button>
              ))}
            </div>
            {vista === "equipos" && (
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", paddingBottom: 8 }}>
                {[[1, "Hoy"], [7, "7 días"], [30, "30 días"]].map(([v, l]) => (
                  <button key={v} style={pill(dias === v)} onClick={() => setDias(v)}>{l}</button>
                ))}
                <button style={btn} onClick={refetch} disabled={loading} title="Actualizar">
                  <RefreshCw size={14} strokeWidth={1.8} />{d ? `Actualizado ${hace((Date.now() - Date.parse(d.generado)) / 60000)}` : "Actualizar"}
                </button>
              </div>
            )}
          </div>

          {vista === "graficas" && (
            <Graficas acc={acc} d={d} nombreDe={nombreDe} info={info} onOpen={abrir} onVerEquipos={() => cambiarVista("equipos")} isMobile={isMobile} />
          )}

          {vista === "equipos" && error && !d && (
            <div style={{ ...card, padding: 18, color: C.clay, fontSize: 14 }}>No se ha podido cargar el estado de los sensores: {error}</div>
          )}
          {vista === "equipos" && !d && !error && <div style={{ ...card, padding: 24, color: C.ink3 }}>Cargando el historial de los equipos…</div>}

          {vista === "equipos" && d && d.equipos.length === 0 && (
            <div style={{ ...card, padding: 20, fontSize: 14, color: C.ink2, lineHeight: 1.6, marginBottom: 20 }}>
              {plan === "completo"
                ? "Todavía no hay equipos registrados. En cuanto un sensor envíe su primer mensaje aparecerá aquí solo (o dalo de alta desde «Instalación»)."
                : "Este alojamiento tiene el Plan Básico: no hay sensores instalados. Abajo tienes el estado de los servicios de datos que usa."}
            </div>
          )}

          {vista === "equipos" && d && d.equipos.length > 0 && <Resumen d={d} dias={dias} info={info} nombreDe={nombreDe} onOpen={abrir} isMobile={isMobile} />}

          {vista === "equipos" && <ServiciosExternos acc={acc} isMobile={isMobile} />}
        </>
      )}
    </div>
  );
}

// ── Gráficas globales por tipo de sensor (la vista para el cliente) ─────────
const LIM_G = { energia: 15, clima: 180, nivel: 360 };
// Colores de serie validados (contraste ≥ 3:1 y distinguibles con daltonismo); siempre en este orden
const COL_EQ = ["#3A7D1C", "#2A78D6", "#E2622B", "#6250D6", "#C93C7A", "#B88400"];
const cambioPct = (a, b) => (!esNum(a) || !esNum(b) || b === 0 ? null : Math.round(((a - b) / Math.abs(b)) * 100));
const TEMA = {
  luz: { c: V.luz, claro: V.luzClaro, osc: V.luzOsc, icon: Zap },
  temp: { c: V.calor, claro: V.calorClaro, osc: V.calorOsc, icon: Thermometer },
  agua: { c: V.agua, claro: V.aguaClaro, osc: V.aguaOsc, icon: Droplets },
  aparatos: { c: V.aparato, claro: V.aparatoClaro, osc: V.aparatoOsc, icon: Power },
  datos: { c: C.ink2, claro: "#E9ECE5", osc: C.ink, icon: Cpu },
};
const EST = {
  ok: [V.ok, V.okClaro, CircleCheck, "Bien"], warn: [V.warn, V.warnClaro, TriangleAlert, "Revisar"],
  bad: [V.bad, V.badClaro, CircleX, "Atención"], info: [C.ink2, "#E9ECE5", Info, "Ahora"],
};
const chip = (col, bg) => ({ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 11px", borderRadius: 14, background: bg, color: col, fontWeight: 800, fontSize: 13, lineHeight: 1.3, maxWidth: "100%" });
// Día de más y de menos de un calendario, en palabras
function extremos(dias, vals, fmtV, mas, menos) {
  const xs = dias.map((d, i) => [d, vals[i]]).filter(([, v]) => esNum(v));
  if (xs.length < 2) return null;
  const hi = xs.reduce((a, b) => (b[1] > a[1] ? b : a)), lo = xs.reduce((a, b) => (b[1] < a[1] ? b : a));
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
      <span style={chip(C.ink, "#F1F3EE")}><ArrowUp size={15} strokeWidth={2.6} />{mas}: {diaCorto(hi[0])} · {fmtV(hi[1])}</span>
      <span style={chip(C.ink, "#F1F3EE")}><ArrowDown size={15} strokeWidth={2.6} />{menos}: {diaCorto(lo[0])} · {fmtV(lo[1])}</span>
    </div>
  );
}
const ESTE = { dia: "este día", semana: "esta semana", mes: "este mes" };
const ANT_CORTO = { dia: "día antes", semana: "sem. anterior", mes: "mes anterior" };
const fmtW = (w) => (!esNum(w) ? "—" : w >= 1000 ? `${nf(w / 1000, 2)} kW` : `${nf(w, 0)} W`);
const mediaDe = (xs) => { const v = xs.filter(esNum); return v.length ? v.reduce((s, x) => s + x, 0) / v.length : null; };
const campoAhora = (e, k) => { const c = (e.campos || []).find((x) => x.campo === k); return c && esNum(c.ultimo) ? Number(c.ultimo) : null; };
// Medidor que manda: el general (no actuador, zona «general»), si no el que más gasta
const medidorPrincipal = (energia) => [...energia].sort((a, b) => (!b.actuador - !a.actuador) || (/general/i.test(b.zona || "") - /general/i.test(a.zona || "")) || b.kwh - a.kwh || b.lecturas - a.lecturas)[0];
const nochesConAguaDe = (g) => {
  const A = g.agua.filter((x) => x.lecturas > 0 || x.litros > 0);
  return A.length ? g.dias.filter((_, j) => A.some((x) => (x.noche_dia[j] || 0) > 0)).length : 0;
};
// 24 valores por hora del reloj: media de lecturas [t, media, …] o suma de incrementos [t, v]
const porHoraMedia = (puntos) => {
  const s = Array(24).fill(0), n = Array(24).fill(0);
  (puntos || []).forEach(([t, v]) => { if (esNum(v)) { const h = new Date(t).getHours(); s[h] += v; n[h]++; } });
  return s.map((x, h) => (n[h] ? x / n[h] : null));
};
const porHoraSuma = (pares) => {
  const s = Array(24).fill(null);
  (pares || []).forEach(([t, v]) => { if (esNum(v)) { const h = new Date(t).getHours(); s[h] = (s[h] || 0) + v; } });
  return s;
};

function Seccion({ tema, titulo, sub, children }) {
  const t = TEMA[tema];
  const I = t.icon;
  return (
    <section style={{ marginBottom: 34 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14, margin: "0 0 14px", padding: "12px 16px", borderRadius: 16, background: t.claro, borderLeft: `6px solid ${t.osc}` }}>
        <span style={{ width: 46, height: 46, borderRadius: 14, background: t.osc, color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: "0 4px 12px rgba(20,30,15,0.18)" }}>
          <I size={23} strokeWidth={2.2} />
        </span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 23, fontWeight: 800, color: t.osc, lineHeight: 1.15 }}>{titulo}</div>
          {sub && <div style={{ fontSize: 14, color: C.ink2, marginTop: 2 }}>{sub}</div>}
        </div>
      </div>
      {children}
    </section>
  );
}
function TarjetaG({ titulo, ayuda, extra, children, style }) {
  return (
    <div style={{ ...card, padding: 18, boxShadow: "0 2px 10px rgba(20,30,15,0.04)", minWidth: 0, ...style }}>
      {(titulo || extra) && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
          <div style={{ minWidth: 0 }}>
            {titulo && <div style={{ fontSize: 16.5, fontWeight: 800, color: C.ink, lineHeight: 1.25 }}>{titulo}</div>}
            {ayuda && <div style={{ fontSize: 13, color: C.ink3, marginTop: 3, lineHeight: 1.4 }}>{ayuda}</div>}
          </div>
          {extra}
        </div>
      )}
      {children}
    </div>
  );
}
function CambioPill({ a, b, antes, menosEsMejor = true }) {
  const p = cambioPct(a, b);
  if (p === null) return null;
  const igual = Math.abs(p) < 1, bien = menosEsMejor ? p < 0 : p > 0;
  const I = igual ? Minus : p < 0 ? ArrowDown : ArrowUp;
  return (
    <span style={chip(igual ? C.ink2 : bien ? V.ok : V.bad, igual ? "#E9ECE5" : bien ? V.okClaro : V.badClaro)}>
      <I size={15} strokeWidth={2.8} />{igual ? `igual que ${antes}` : `${Math.abs(p)} % ${p < 0 ? "menos" : "más"} que ${antes}`}
    </span>
  );
}
function Cambio({ a, b, menosEsMejor = true }) {
  const p = cambioPct(a, b);
  if (p === null) return null;
  const bien = menosEsMejor ? p <= 0 : p >= 0;
  return <span style={{ color: p === 0 ? C.ink2 : bien ? V.ok : V.bad, fontWeight: 800 }}>{p === 0 ? "igual" : `${p > 0 ? "▲" : "▼"} ${Math.abs(p)} % ${p > 0 ? "más" : "menos"}`}</span>;
}
function ChipConfort({ pct }) {
  if (!esNum(pct)) return null;
  const e = pct >= 80 ? "ok" : pct >= 50 ? "warn" : "bad";
  const I = e === "ok" ? Smile : e === "warn" ? Meh : Frown;
  return <span style={chip(EST[e][0], EST[e][1])}><I size={16} strokeWidth={2.4} />Cómoda el {nf(pct, 0)} % del tiempo</span>;
}
function Vacio({ children }) {
  return <div style={{ fontSize: 14, color: C.ink2, padding: "18px 4px" }}>{children}</div>;
}
function Leyenda({ items }) {
  return (
    <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 10, fontSize: 13, color: C.ink2 }}>
      {items.map((it) => (
        <span key={it.label} style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
          <span style={{ width: 18, height: 6, borderRadius: 3, background: it.color, display: "inline-block" }} />
          <b style={{ color: C.ink }}>{it.label}</b>{it.extra ? <span style={{ color: C.ink2 }}>{it.extra}</span> : null}
        </span>
      ))}
    </div>
  );
}
const EJE = { fontSize: 12, fill: C.ink2, fontWeight: 600 };
function GraficaLineas({ series, t0, t1, unidad, limMin, bucketMin, banda, dominio, alto = 260 }) {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, "");
  const thr = Math.max(limMin * 60000, 3 * bucketMin * 60000);
  const filas = construirFilas(series, null, thr, 1, false);
  const span = t1 - t0;
  if (!filas.length) return <Vacio>Sin lecturas en este periodo.</Vacio>;
  const Tip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const r = payload[0].payload;
    return (
      <div style={{ background: "#fff", border: `1.5px solid ${C.ink3}`, borderRadius: 10, padding: "8px 10px", fontSize: 13, boxShadow: "0 6px 18px rgba(0,0,0,0.12)" }}>
        <div style={{ color: C.ink2, fontWeight: 700, marginBottom: 4 }}>{fechaHora(r.t)}</div>
        {series.map((s) => esNum(r[s.key]) && (
          <div key={s.key} style={{ display: "flex", alignItems: "center", gap: 6, color: C.ink }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: s.color }} />{s.label}: <b>{fmt(r[s.key], unidad)}</b>
          </div>
        ))}
      </div>
    );
  };
  const una = series.length === 1;
  return (
    <div style={{ width: "100%", height: alto }}>
      <ResponsiveContainer>
        <ComposedChart data={filas} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
          <defs>
            {una && (
              <linearGradient id={`ga${uid}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor={series[0].color} stopOpacity={0.32} /><stop offset="1" stopColor={series[0].color} stopOpacity={0.03} />
              </linearGradient>
            )}
          </defs>
          <CartesianGrid stroke="#E3E7DF" vertical={false} />
          <XAxis dataKey="t" type="number" domain={[t0, t1]} ticks={ticksTiempo(t0, t1)} tickFormatter={fmtTick(span)} tick={EJE} stroke="#B9BFB3" allowDataOverflow />
          <YAxis width={52} tick={EJE} stroke="#B9BFB3" tickFormatter={(v) => nf(v, decimales(unidad))} domain={dominio || ["auto", "auto"]} />
          {banda && <ReferenceArea y1={banda[0]} y2={banda[1]} fill={V.confort} fillOpacity={0.13} stroke={V.confort} strokeOpacity={0.35} strokeDasharray="4 4" ifOverflow="extendDomain" />}
          <Tooltip content={<Tip />} cursor={{ stroke: C.ink3, strokeWidth: 1 }} />
          {una && <Area dataKey={series[0].key} stroke="none" fill={`url(#ga${uid})`} isAnimationActive={false} connectNulls={false} activeDot={false} />}
          {series.map((s) => <Line key={s.key} dataKey={s.key} stroke={s.color} strokeWidth={2.6} dot={s.puntos.length < 40 ? { r: 3.5, fill: s.color, stroke: "#fff", strokeWidth: 1.5 } : false} activeDot={{ r: 5, stroke: "#fff", strokeWidth: 2 }} isAnimationActive={false} connectNulls={false} />)}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
// Periodos como en «Consumo y costes»: un día, una semana (lunes a domingo) o un mes, y se navega con ‹ ›
const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
const MODOS = [["dia", "Día"], ["semana", "Semana"], ["mes", "Mes"]];
const ANTERIOR = { dia: "el día anterior", semana: "la semana anterior", mes: "el mes anterior" };
const FRENTE = { dia: "Frente al día anterior", semana: "Frente a la semana anterior", mes: "Frente al mes anterior" };
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const deYmd = (s) => new Date(s + "T12:00:00");
function periodoDe(modo, ancla) {
  const a = deYmd(ancla);
  if (modo === "dia") { const p = new Date(a); p.setDate(p.getDate() - 1); return { desde: ancla, hasta: ancla, pdesde: ymd(p) }; }
  if (modo === "semana") {
    const lu = new Date(a); lu.setDate(a.getDate() - ((a.getDay() + 6) % 7));
    const dom = new Date(lu); dom.setDate(lu.getDate() + 6);
    const ant = new Date(lu); ant.setDate(lu.getDate() - 7);
    return { desde: ymd(lu), hasta: ymd(dom), pdesde: ymd(ant) };
  }
  return { desde: ymd(new Date(a.getFullYear(), a.getMonth(), 1, 12)), hasta: ymd(new Date(a.getFullYear(), a.getMonth() + 1, 0, 12)), pdesde: ymd(new Date(a.getFullYear(), a.getMonth() - 1, 1, 12)) };
}
function moverAncla(modo, ancla, paso) {
  const a = deYmd(ancla);
  if (modo === "dia") a.setDate(a.getDate() + paso);
  else if (modo === "semana") a.setDate(a.getDate() + 7 * paso);
  else { a.setDate(1); a.setMonth(a.getMonth() + paso); }
  return ymd(a);
}
function etiquetaPeriodo(modo, p) {
  const a = deYmd(p.desde), b = deYmd(p.hasta);
  if (modo === "dia") { const s = a.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long", year: "numeric" }); return s.charAt(0).toUpperCase() + s.slice(1); }
  if (modo === "semana") return `Semana del ${a.getDate()}${a.getMonth() !== b.getMonth() ? ` de ${MESES[a.getMonth()]}` : ""} al ${b.getDate()} de ${MESES[b.getMonth()]} de ${b.getFullYear()}`;
  return `${MESES[a.getMonth()].charAt(0).toUpperCase()}${MESES[a.getMonth()].slice(1)} de ${a.getFullYear()}`;
}
const HORAS = Array.from({ length: 24 }, (_, h) => `${h} h`);

// Barras por hora (un día) o por día (semana/mes), una serie por equipo; los días futuros quedan vacíos
function filasTiempo(g, items, claveHora, claveDia, factor = 1) {
  const t0 = Date.parse(g.desde), t1 = Math.min(Date.parse(g.hasta), Date.now());
  if (items.some((x) => x[claveHora])) {
    const maps = items.map((x) => new Map(x[claveHora] || []));
    const out = [];
    for (let h = Math.floor(t0 / HORA) * HORA; h < Date.parse(g.hasta); h += HORA) {
      const r = { etq: `${pad(new Date(h).getHours())}:00` };
      maps.forEach((m, i) => { r["s" + i] = h < t1 ? (m.get(h) || 0) * factor : null; });
      out.push(r);
    }
    return { filas: out, porHora: true };
  }
  const hoy = hoyStr();
  return {
    filas: g.dias.map((dia, j) => {
      const r = { etq: g.dias.length > 7 ? String(deYmd(dia).getDate()) : diaCorto(dia) };
      items.forEach((x, i) => { r["s" + i] = dia > hoy ? null : ((x[claveDia] || [])[j] || 0) * factor; });
      return r;
    }),
    porHora: false,
  };
}
// Gráfica por días o por horas del día (categorías), con líneas, banda mín-máx y barras con relieve
function GraficaCategorias({ filas, series, unidad, banda, refMedia, alto = 240, tipo = "linea" }) {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, "");
  return (
    <div style={{ width: "100%", height: alto }}>
      <ResponsiveContainer>
        <ComposedChart data={filas} margin={{ top: 14, right: 12, bottom: 0, left: 0 }}>
          <defs>
            {series.map((s) => (
              <linearGradient key={s.key} id={`gb${uid}${s.key}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor={mezcla(s.color, "#FFFFFF", 0.18)} /><stop offset="1" stopColor={mezcla(s.color, "#000000", 0.18)} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid stroke="#E3E7DF" vertical={false} />
          <XAxis dataKey="etq" tick={EJE} stroke="#B9BFB3" minTickGap={8} interval="preserveStartEnd" />
          <YAxis width={52} tick={EJE} stroke="#B9BFB3" tickFormatter={(v) => nf(v, decimales(unidad))} domain={tipo === "barras" ? [0, "auto"] : ["auto", "auto"]} />
          {banda && <ReferenceArea y1={banda[0]} y2={banda[1]} fill={V.confort} fillOpacity={0.13} stroke={V.confort} strokeOpacity={0.35} strokeDasharray="4 4" ifOverflow="extendDomain" />}
          {esNum(refMedia) && refMedia > 0 && (
            <ReferenceLine y={refMedia} stroke={C.ink} strokeWidth={1.5} strokeDasharray="6 4" label={{ value: `media ${fmt(refMedia, unidad)}`, position: "insideTopRight", fontSize: 12, fontWeight: 700, fill: C.ink }} />
          )}
          <Tooltip formatter={(v, name) => [Array.isArray(v) ? `${fmt(v[0], unidad)} a ${fmt(v[1], unidad)}` : fmt(v, unidad), name]} labelStyle={{ color: C.ink, fontWeight: 700 }}
            contentStyle={{ borderRadius: 10, border: `1.5px solid ${C.ink3}`, fontSize: 13 }} cursor={{ fill: "rgba(31,39,29,0.07)" }} />
          {series.map((s) => (s.banda
            ? <Area key={s.key} dataKey={s.key} name={s.label} stroke="none" fill={s.color} fillOpacity={0.2} isAnimationActive={false} connectNulls />
            : tipo === "barras"
              ? <Bar key={s.key} dataKey={s.key} name={s.label} fill={`url(#gb${uid}${s.key})`} radius={[5, 5, 0, 0]} isAnimationActive={false} />
              : <Line key={s.key} dataKey={s.key} name={s.label} stroke={s.color} strokeWidth={2.6} dot={{ r: 3, fill: s.color, stroke: "#fff", strokeWidth: 1.2 }} isAnimationActive={false} connectNulls />))}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
function TablaResumen({ cabecera, filas }) {
  return (
    <div style={{ overflowX: "auto", marginTop: 6 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5, minWidth: 560 }}>
        <thead>
          <tr style={{ background: "#F1F3EE" }}>{cabecera.map((h, i) => <th key={h} style={{ fontSize: 12, letterSpacing: 0.3, color: C.ink, textAlign: i ? "right" : "left", padding: "8px 10px", fontWeight: 800 }}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {filas.map((f, j) => (
            <tr key={j} style={{ borderTop: `1px solid ${C.line}` }}>
              {f.map((v, i) => <td key={i} style={{ padding: "9px 10px", textAlign: i ? "right" : "left", color: C.ink, fontWeight: i === 1 ? 800 : 500, whiteSpace: i ? "nowrap" : "normal" }}>{v}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
const cuando = (iso) => (iso ? <span style={{ color: C.ink3, fontWeight: 500 }}> ({fechaHora(iso)})</span> : null);

// ── «Ahora mismo»: la casa dibujada con lo último que han mandado los sensores ──
function FilaVeredicto({ f, primera }) {
  const [col, bg, I, txt] = EST[f.estado];
  const Ic = f.icon;
  return (
    <div style={{ display: "flex", gap: 12, alignItems: "center", padding: "11px 0", borderTop: primera ? "none" : `1px solid ${C.line}` }}>
      <span style={{ width: 42, height: 42, borderRadius: 13, background: f.color, color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Ic size={21} strokeWidth={2.3} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 16.5, fontWeight: 800, color: C.ink, lineHeight: 1.2 }}>{f.titulo}</div>
        <div style={{ fontSize: 13.5, color: C.ink2, lineHeight: 1.4, marginTop: 2 }}>
          {f.texto}
          {f.accion && <> · <button onClick={f.accion} style={{ border: "none", background: "none", padding: 0, color: V.bad, fontWeight: 800, cursor: "pointer", fontSize: 13.5, fontFamily: "var(--font-sans)", textDecoration: "underline" }}>ver cuáles</button></>}
        </div>
      </div>
      <span style={{ ...chip(col, bg), flexShrink: 0 }}><I size={15} strokeWidth={2.6} />{txt}</span>
    </div>
  );
}
function AhoraMismo({ d, g, nombreDe, onVerEquipos, isMobile }) {
  const eqs = d.equipos;
  const fresco = (e) => e.estado === "ok";
  const desde = (e) => hace(e.min_sin_datos);
  const masReciente = (xs) => [...xs].sort((a, b) => (a.min_sin_datos ?? 1e12) - (b.min_sin_datos ?? 1e12))[0];
  const filas = [], badges = [];

  const cl = eqs.filter((e) => e.tipo === "clima" && campoAhora(e, "temperature") !== null);
  const clF = cl.filter(fresco);
  const ref = cl[0];
  const cf = {
    tmin: (ref && campoAhora(ref, "comfort_temperature_min")) ?? 20, tmax: (ref && campoAhora(ref, "comfort_temperature_max")) ?? 25,
    hmin: (ref && campoAhora(ref, "comfort_humidity_min")) ?? 40, hmax: (ref && campoAhora(ref, "comfort_humidity_max")) ?? 60,
  };
  if (clF.length) {
    const t = mediaDe(clF.map((e) => campoAhora(e, "temperature")));
    const est = estadoTemp(t, cf);
    const col = { confort: V.confort, frio: V.frio, calor: V.calor }[est];
    filas.push({
      icon: Thermometer, color: col, estado: est === "confort" ? "ok" : "warn", titulo: `${nf(t, 1)} °C dentro`,
      texto: (est === "confort" ? `Temperatura cómoda (lo cómodo es de ${cf.tmin} a ${cf.tmax} °C)` : est === "frio" ? `Hace frío: por debajo de ${cf.tmin} °C` : `Hace calor: por encima de ${cf.tmax} °C`)
        + (clF.length > 1 ? ` · ${clF.map((e) => `${nombreDe(e.device)} ${nf(campoAhora(e, "temperature"), 1)} °C`).join(" · ")}` : ""),
    });
    const h = mediaDe(clF.map((e) => campoAhora(e, "humidity")));
    if (h !== null) {
      filas.push({ icon: Droplet, color: V.agua, estado: h > cf.hmax || h < cf.hmin ? "warn" : "ok", titulo: `${nf(h, 0)} % de humedad`,
        texto: h > cf.hmax ? "Humedad alta: conviene ventilar" : h < cf.hmin ? "Ambiente seco" : `Humedad correcta (de ${cf.hmin} a ${cf.hmax} %)` });
    }
    badges.push({ donde: "clima", color: col, valor: `${nf(t, 1)} °C${h !== null ? ` · ${nf(h, 0)} %` : ""}`, texto: { confort: "temperatura cómoda", frio: "hace frío", calor: "hace calor" }[est] });
  } else if (cl.length) {
    const e = masReciente(cl);
    filas.push({ icon: Thermometer, color: V.bad, estado: "bad", titulo: "Temperatura sin datos nuevos",
      texto: `${cl.length > 1 ? "Los sensores no envían" : "El sensor no envía"} desde ${desde(e)}; la última lectura fue ${nf(campoAhora(e, "temperature"), 1)} °C` });
    badges.push({ donde: "clima", color: V.bad, valor: `${nf(campoAhora(e, "temperature"), 1)} °C`, texto: `dato de ${desde(e)}` });
  }

  const med = eqs.filter((e) => String(e.tipo).startsWith("energia") && !e.actuador && e.estado !== "sin_datos");
  let consumo = false;
  if (med.length) {
    const princ = g && g.energia.length ? medidorPrincipal(g.energia) : null;
    const medF = med.filter(fresco);
    const p = medF.find((e) => princ && e.device === princ.device) || medF[0];
    if (p) {
      const w = campoAhora(p, "power");
      consumo = esNum(w) && w > 5;
      filas.push({ icon: Zap, color: V.luz, estado: "info", titulo: `${fmtW(w)} de luz ahora`, texto: `Lo que se está gastando en este momento · ${nombreDe(p.device)}` });
      badges.push({ donde: "luz", color: V.luzOsc, valor: fmtW(w), texto: "luz ahora" });
    } else {
      const e = masReciente(med);
      filas.push({ icon: Zap, color: V.bad, estado: "bad", titulo: "Luz sin datos nuevos", texto: `${med.length > 1 ? "Los medidores no envían" : "El medidor no envía"} desde ${desde(e)}` });
      badges.push({ donde: "luz", color: V.bad, valor: "sin datos", texto: desde(e) });
    }
  }

  const dep = eqs.find((e) => e.tipo === "nivel_agua" && campoAhora(e, "liquid_level_percent") !== null);
  if (dep) {
    const n = campoAhora(dep, "liquid_level_percent"), mini = campoAhora(dep, "mini_set") ?? 10;
    if (fresco(dep)) {
      const est = n <= mini ? "bad" : n <= 25 ? "warn" : "ok";
      filas.push({ icon: Gauge, color: est === "bad" ? V.bad : V.agua, estado: est, titulo: `Depósito al ${nf(n, 0)} %`,
        texto: est === "bad" ? "Casi vacío: conviene revisarlo o rellenarlo" : est === "warn" ? "Nivel bajo" : "Nivel correcto" });
      badges.push({ donde: "agua", color: est === "bad" ? V.bad : V.aguaOsc, valor: `${nf(n, 0)} %`, texto: est === "bad" ? "depósito casi vacío" : "depósito" });
    } else {
      filas.push({ icon: Gauge, color: V.bad, estado: "bad", titulo: "Depósito sin datos nuevos", texto: `El sensor no envía desde ${desde(dep)} (último: ${nf(n, 0)} %)` });
      badges.push({ donde: "agua", color: V.bad, valor: `${nf(n, 0)} %`, texto: `dato de ${desde(dep)}` });
    }
  }
  const cont = eqs.filter((e) => e.tipo === "pulsos_agua");
  if (cont.length) {
    const noches = g ? nochesConAguaDe(g) : 0;
    if (noches >= 2) filas.push({ icon: Droplets, color: "#C77700", estado: "warn", titulo: "Posible fuga de agua", texto: `Se ha gastado agua de madrugada (de 2 a 5 h) ${noches} noches en el periodo que estás viendo` });
    else if (!cont.some(fresco)) filas.push({ icon: Droplets, color: V.bad, estado: "bad", titulo: "Contador de agua sin datos nuevos", texto: `No envía desde ${desde(masReciente(cont))}` });
    else filas.push({ icon: Droplets, color: V.agua, estado: "ok", titulo: "Sin señales de fuga", texto: "No se ha gastado agua de madrugada" });
  }

  const act = eqs.filter((e) => e.tipo === "actuador" || e.actuador);
  let luces = false;
  if (act.length) {
    const on = act.filter((e) => e.textos && e.textos.state === "ON");
    const onF = on.filter(fresco), dudosos = on.filter((e) => !fresco(e));
    luces = onF.some((e) => !/valv|grifo|riego|plug|enchufe/i.test(`${e.device} ${nombreDe(e.device)}`));
    filas.push({
      icon: Power, color: V.aparato, estado: "info",
      titulo: onF.length ? `${onF.length} ${onF.length === 1 ? "aparato encendido" : "aparatos encendidos"}` : "Todo apagado",
      texto: (onF.length ? onF.map((e) => nombreDe(e.device)).join(", ") : `${act.length} ${act.length === 1 ? "aparato" : "aparatos"} que se manejan desde la app`)
        + (dudosos.length ? ` · ${dudosos.map((e) => nombreDe(e.device)).join(", ")} estaba encendido, pero no envía desde ${desde(masReciente(dudosos))}` : ""),
    });
    badges.push({ donde: "aparatos", color: V.aparatoOsc, valor: onF.length ? `${onF.length} encendido${onF.length > 1 ? "s" : ""}` : "todo apagado", texto: onF.length ? nombreDe(onF[0].device) : "aparatos" });
  }

  const r = d.resumen;
  const mal = (r.caidos || 0) + (r.sin_datos || 0);
  filas.push(mal
    ? { icon: Cpu, color: V.bad, estado: "bad", titulo: `${mal} de ${r.equipos} equipos sin enviar datos`, texto: "Por eso puede haber huecos en las gráficas", accion: onVerEquipos }
    : { icon: Cpu, color: V.ok, estado: "ok", titulo: "Todos los equipos envían datos", texto: `${r.equipos} equipos conectados` });

  const hNow = new Date().getHours();
  const noche = hNow >= 21 || hNow < 7;
  const reloj = new Date().toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
  const n = dep ? campoAhora(dep, "liquid_level_percent") : null;
  return (
    <div style={{ borderRadius: 22, padding: isMobile ? 14 : 22, marginBottom: 22, border: `1px solid ${noche ? "#1B2540" : "#C4DBEC"}`,
      background: noche ? "linear-gradient(180deg, #18223B 0%, #2B3B60 100%)" : "linear-gradient(180deg, #D3EAFB 0%, #EEF7FE 55%, #FFFFFF 100%)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 25, fontWeight: 800, color: noche ? "#FFFFFF" : C.ink }}>
          <span style={{ width: 11, height: 11, borderRadius: 99, background: "#3FBF3F", boxShadow: "0 0 0 4px rgba(63,191,63,0.25)" }} />Ahora mismo
        </span>
        <span style={{ fontSize: 14, fontWeight: 600, color: noche ? "#D3DBEE" : C.ink2 }}>{reloj} · lo último que han enviado los sensores</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "minmax(0, 1.05fr) minmax(0, 1fr)", gap: isMobile ? 12 : 22, alignItems: "center" }}>
        <Casa3D luces={luces} consumo={consumo} nivel={n} deposito={!!dep} noche={noche} badges={badges} conBadges={!isMobile} alto={isMobile ? 250 : 360} />
        <div style={{ background: "#FFFFFF", borderRadius: 16, padding: "4px 16px", boxShadow: "0 10px 28px rgba(20,30,15,0.14)" }}>
          {filas.map((f, i) => <FilaVeredicto key={i} f={f} primera={i === 0} />)}
        </div>
      </div>
    </div>
  );
}

// Pieza del resumen del periodo: dibujo arriba, cifra grande y una o dos líneas
function Pieza({ tema, titulo, dibujo, valor, lineas, pie, onClick }) {
  const t = TEMA[tema];
  const I = t.icon;
  return (
    <div onClick={onClick} style={{ ...card, padding: 0, overflow: "hidden", cursor: onClick ? "pointer" : "default", display: "flex", flexDirection: "column", boxShadow: "0 4px 16px rgba(20,30,15,0.07)", minWidth: 0 }}>
      <div style={{ background: t.osc, color: "#fff", padding: "9px 14px", fontWeight: 800, fontSize: 14.5, display: "flex", alignItems: "center", gap: 8 }}>
        <I size={17} strokeWidth={2.4} />{titulo}
      </div>
      <div style={{ background: `linear-gradient(180deg, ${t.claro} 0%, #FFFFFF 100%)`, display: "flex", justifyContent: "center", alignItems: "flex-end", padding: "12px 10px 4px", height: 136 }}>{dibujo}</div>
      <div style={{ padding: "6px 16px 14px", flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
        <div style={{ fontSize: 30, fontWeight: 800, color: C.ink, lineHeight: 1.05 }}>{valor}</div>
        {lineas.filter(Boolean).map((l, i) => <div key={i} style={{ fontSize: 13.5, color: C.ink2, lineHeight: 1.35 }}>{l}</div>)}
        {pie && <div style={{ marginTop: 6 }}>{pie}</div>}
      </div>
    </div>
  );
}
function MiniDato({ icon: I, color, l, v }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 13, color: C.ink2, background: "#F4F6F2", borderRadius: 8, padding: "3px 8px" }}>
      <I size={14} strokeWidth={2.4} color={color} />{l} <b style={{ color: C.ink }}>{v}</b>
    </span>
  );
}

function Graficas({ acc, d, nombreDe, onOpen, onVerEquipos, isMobile }) {
  const [modo, setModo] = useState(() => { try { return localStorage.getItem("er_graficas_modo") || "semana"; } catch (e) { return "semana"; } });
  const [ancla, setAncla] = useState(hoyStr());
  const elegirModo = (m) => { setModo(m); try { localStorage.setItem("er_graficas_modo", m); } catch (e) { /* sin almacenamiento */ } };
  const per = periodoDe(modo, ancla);
  const siguiente = periodoDe(modo, moverAncla(modo, ancla, 1));
  const haySiguiente = siguiente.desde <= hoyStr();
  const rk = `${per.desde}|${per.hasta}|${per.pdesde}`;
  const { data, error, loading, refetch } = useApi(() => api.getGraficas(acc.locationID, per), [acc.locationID, rk], { pollMs: 120000 });
  const g = data && data.locationID === acc.locationID && data.dias && data.dias[0] === per.desde ? data : null;

  const flecha = (on) => ({ width: 42, height: 42, borderRadius: 999, border: `1.5px solid ${C.ink3}`, background: "#fff", color: C.ink, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: on ? "pointer" : "default", opacity: on ? 1 : 0.35, flexShrink: 0 });
  const ahora = d && d.equipos.length > 0 ? <AhoraMismo d={d} g={g} nombreDe={nombreDe} onVerEquipos={onVerEquipos} isMobile={isMobile} /> : null;
  const controles = (
    <div style={{ ...card, padding: 12, marginBottom: 18, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", boxShadow: "0 2px 10px rgba(20,30,15,0.05)" }}>
      <div style={{ display: "flex", gap: 4, background: "#EEF0EB", borderRadius: 999, padding: 4, flex: isMobile ? "1 1 100%" : "0 0 auto" }}>
        {MODOS.map(([m, l]) => <button key={m} style={{ ...pill(modo === m), border: "none", padding: "8px 16px", fontSize: 14, flex: isMobile ? 1 : "none" }} onClick={() => elegirModo(m)}>{l}</button>)}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flex: isMobile ? "1 1 100%" : 1, justifyContent: "center", minWidth: isMobile ? 0 : 260 }}>
        <button style={flecha(true)} onClick={() => setAncla(moverAncla(modo, ancla, -1))} aria-label="Periodo anterior"><ChevronLeft size={22} strokeWidth={2.4} /></button>
        <span style={{ fontWeight: 800, color: C.ink, fontSize: isMobile ? 15 : 17, textAlign: "center", minWidth: isMobile ? 0 : 220, flex: isMobile ? 1 : "none" }}>{etiquetaPeriodo(modo, per)}</span>
        <button style={flecha(haySiguiente)} disabled={!haySiguiente} onClick={() => setAncla(moverAncla(modo, ancla, 1))} aria-label="Periodo siguiente"><ChevronRight size={22} strokeWidth={2.4} /></button>
      </div>
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <button style={pill(per.desde <= hoyStr() && hoyStr() <= per.hasta)} onClick={() => setAncla(hoyStr())}>Hoy</button>
        <input type="date" value={ancla} max={hoyStr()} onChange={(e) => e.target.value && setAncla(e.target.value)} style={{ ...input, borderColor: C.ink3 }} aria-label="Elegir fecha" />
        <button style={btn} onClick={refetch} disabled={loading} title="Actualizar"><RefreshCw size={14} strokeWidth={2} /></button>
      </div>
    </div>
  );
  if (error && !g) return <>{ahora}{controles}<div style={{ ...card, padding: 18, color: V.bad }}>No se han podido cargar las gráficas: {error}</div></>;
  if (!g) return <>{ahora}{controles}<div style={{ ...card, padding: 24, color: C.ink2 }}>Preparando las gráficas…</div></>;

  const t0 = Date.parse(g.desde), t1 = Date.parse(g.hasta);
  const esDia = modo === "dia";
  const periodo = ESTE[modo];
  const antes = ANTERIOR[modo];
  const hoy = hoyStr();
  const nom = (x) => nombreDe(x.device);
  const sub = (x) => [x.zona, x.ultimo ? `último dato ${hace((Date.now() - Date.parse(x.ultimo)) / 60000)}` : null].filter(Boolean).join(" · ");
  const ninguno = !g.energia.length && !g.clima.length && !g.agua.length && !g.deposito.length && !g.aparatos.length;
  const vsAntes = (a, b, menosEsMejor = true) => (cambioPct(a, b) !== null ? <Cambio a={a} b={b} menosEsMejor={menosEsMejor} /> : <span style={{ color: C.ink3 }}>—</span>);
  // Diferencia en palabras («1,6 °C menos que…») o corta para las tablas («−1,6 °C»)
  const difTexto = (a, b, u) => { const df = a - b; return Math.abs(df) < 0.05 ? `igual que ${antes}` : `${nf(Math.abs(df), 1)} ${u} ${df > 0 ? "más" : "menos"} que ${antes}`; };
  const difAbs = (a, b, u) => { if (!esNum(a) || !esNum(b)) return "—"; const df = a - b; return Math.abs(df) < 0.05 ? "igual" : `${df > 0 ? "+" : "−"}${nf(Math.abs(df), 1)} ${u}`; };
  const etqDia = (dia) => (g.dias.length > 7 ? String(deYmd(dia).getDate()) : diaCorto(dia));

  // Luz
  const E = g.energia.filter((x) => x.lecturas > 0 || x.kwh > 0);
  const enKw = Math.max(0, ...E.map((x) => (x.potencia_stat ? x.potencia_stat.max : 0))) >= 2000;
  const uP = enKw ? "kW" : "W", fP = enKw ? 0.001 : 1;
  const colE = (x) => COL_EQ[E.indexOf(x) % COL_EQ.length];
  const seriesP = E.filter((x) => x.potencia.length).map((x) => ({
    key: "s" + E.indexOf(x), label: nom(x), color: colE(x), x, puntos: x.potencia.map(([t, a, mn, mx]) => [t, a * fP, mn * fP, mx * fP]),
  }));
  const Ek = E.filter((x) => x.kwh > 0);
  const barrasE = Ek.length ? filasTiempo(g, Ek, "kwh_hora", "kwh_dia") : null;
  const hayKwh = Ek.length > 0;
  const general = E.length ? medidorPrincipal(E) : null;
  const princE = hayKwh ? (general && general.kwh > 0 ? general : Ek[0]) : null;
  const mediaDiaKwh = (x) => (x.dias_con_datos ? x.kwh / x.dias_con_datos : null);
  const potenciaViva = E.some((x) => x.potencia_stat && x.potencia_stat.max > 0);
  const relojE = princE
    ? (esDia ? porHoraSuma(princE.kwh_hora) : princE.perfil_kwh)
    : potenciaViva && general ? (esDia ? porHoraMedia(general.potencia).map((v) => (esNum(v) ? v * fP : null)) : (general.perfil_potencia || []).map((v) => (esNum(v) ? v * fP : null))) : null;
  const hayRelojE = relojE && relojE.some((v) => esNum(v) && v > 0);
  const kwhDia = princE ? g.dias.map((dia, j) => (dia > hoy ? null : !princE.potencia_dia[j] && !princE.kwh_dia[j] ? null : princE.kwh_dia[j] || 0)) : null;
  const maxKwhDia = kwhDia ? Math.max(1e-9, ...kwhDia.filter(esNum)) : 1;
  const colorKwh = (v) => mezcla("#FFF4D2", "#9A5A00", v / maxKwhDia);
  // Temperatura y humedad
  const Cl = g.clima.filter((x) => x.lecturas > 0 && x.temp);
  const conf = (Cl.find((x) => x.confort) || {}).confort || { tmin: 20, tmax: 25, hmin: 40, hmax: 60 };
  const colCl = (i) => COL_EQ[i % COL_EQ.length];
  const tMedia = mediaDe(Cl.map((x) => x.temp.media)), hMedia = mediaDe(Cl.filter((x) => x.hum).map((x) => x.hum.media));
  const tMediaAntes = mediaDe(Cl.map((x) => x.temp_previo && x.temp_previo.media));
  const confPct = mediaDe(Cl.map((x) => x.confort && x.confort.temp_pct));
  const tDia = g.dias.map((_, j) => mediaDe(Cl.map((x) => x.temp_dia[j] && x.temp_dia[j].media)));
  const relojT = Cl.length ? (esDia
    ? (() => { const hs = Cl.map((x) => porHoraMedia(x.temperatura)); return HORAS.map((_, k) => mediaDe(hs.map((h) => h[k]))); })()
    : HORAS.map((_, k) => mediaDe(Cl.map((x) => x.perfil_temp && x.perfil_temp[k])))) : null;
  const hayRelojT = relojT && relojT.some(esNum);
  const frioH = hayRelojT ? relojT.reduce((b, v, i) => (esNum(v) && (b === null || v < relojT[b]) ? i : b), null) : null;
  // Agua
  const A = g.agua.filter((x) => x.lecturas > 0 || x.litros > 0);
  const colA = (i) => COL_EQ[(i + 1) % COL_EQ.length];
  const litros = A.reduce((s, x) => s + (x.litros || 0), 0), litrosPrev = A.reduce((s, x) => s + (x.litros_previo || 0), 0);
  const diasAgua = Math.max(0, ...A.map((x) => x.dias_con_datos || 0));
  const nochesConAgua = nochesConAguaDe(g);
  const barrasA = A.some((x) => x.litros > 0) ? filasTiempo(g, A, "litros_hora", "litros_dia") : null;
  const litrosDia = barrasA ? g.dias.map((dia, j) => (dia > hoy ? null : A.reduce((s, x) => s + (x.litros_dia[j] || 0), 0))) : null;
  const maxLitrosDia = litrosDia ? Math.max(1e-9, ...litrosDia.filter(esNum)) : 1;
  const relojA = barrasA ? (esDia
    ? (() => { const hs = A.map((x) => porHoraSuma(x.litros_hora)); return HORAS.map((_, k) => { const v = hs.map((h) => h[k]).filter(esNum); return v.length ? v.reduce((s, x) => s + x, 0) : null; }); })()
    : HORAS.map((_, k) => { const v = A.map((x) => x.perfil_litros && x.perfil_litros[k]).filter(esNum); return v.length ? v.reduce((s, x) => s + x, 0) : null; })) : null;
  const Dp = g.deposito.filter((x) => x.lecturas > 0);
  // Aparatos
  const Ap = g.aparatos;
  const esValv = (x) => /valv|grifo|riego/i.test(`${x.device} ${nom(x)}`);
  const onTotal = Ap.reduce((s, x) => s + (x.on_min || 0), 0), onPrevTotal = Ap.reduce((s, x) => s + (x.on_min_previo || 0), 0);
  const barrasAp = !esDia && Ap.some((x) => x.on_min > 0)
    ? g.dias.map((dia, j) => { const r = { etq: etqDia(dia) }; Ap.forEach((x, i) => { r["s" + i] = dia > hoy ? null : Math.round(((x.on_dia[j] || 0) / 60) * 10) / 10; }); return r; })
    : null;
  const dos = (a, b) => (isMobile || !a || !b ? "1fr" : `minmax(0, ${a}fr) minmax(0, ${b}fr)`);

  // Resumen del periodo con dibujos
  const piezas = [];
  if (g.energia.length) piezas.push(
    <Pieza key="luz" tema="luz" titulo="Luz consumida"
      dibujo={hayKwh ? <Cilindros actual={princE.kwh} anterior={princE.kwh_previo} color="#E8A500" etqActual={periodo} etqAnterior={ANT_CORTO[modo]} unidad="kWh" alto={120} dec={princE.kwh < 10 ? 1 : 0} />
        : <Bombilla on={false} alto={96} />}
      valor={hayKwh ? `${nf(princE.kwh, princE.kwh < 10 ? 2 : 1)} kWh` : "0 kWh"}
      lineas={hayKwh ? [nom(princE), !esDia && esNum(mediaDiaKwh(princE)) ? <>media <b style={{ color: C.ink }}>{nf(mediaDiaKwh(princE), 2)} kWh</b> al día</> : null] : ["Ningún medidor ha registrado consumo"]}
      pie={hayKwh ? <CambioPill a={princE.kwh} b={princE.kwh_previo} antes={antes} /> : null} />,
  );
  if (Cl.length) piezas.push(
    <Pieza key="t" tema="temp" titulo="Temperatura dentro" dibujo={<Termometro3D valor={tMedia} tmin={conf.tmin} tmax={conf.tmax} alto={122} />}
      valor={`${nf(tMedia, 1)} °C`} lineas={[<>de media · de <b style={{ color: V.frioOsc }}>{nf(Math.min(...Cl.map((x) => x.temp.min)), 1)}</b> a <b style={{ color: V.calorOsc }}>{nf(Math.max(...Cl.map((x) => x.temp.max)), 1)} °C</b></>, esNum(tMediaAntes) ? difTexto(tMedia, tMediaAntes, "°C") : null]}
      pie={<ChipConfort pct={confPct} />} />,
  );
  if (Cl.length && esNum(hMedia)) piezas.push(
    <Pieza key="h" tema="agua" titulo="Humedad dentro" dibujo={<Gota pct={hMedia} alto={118} />} valor={`${nf(hMedia, 0)} %`}
      lineas={[`de media · lo cómodo es de ${conf.hmin} a ${conf.hmax} %`]}
      pie={(() => { const e = hMedia > conf.hmax || hMedia < conf.hmin ? "warn" : "ok"; const I = EST[e][2]; return <span style={chip(EST[e][0], EST[e][1])}><I size={15} strokeWidth={2.6} />{hMedia > conf.hmax ? "Demasiado húmedo" : hMedia < conf.hmin ? "Ambiente seco" : "Humedad correcta"}</span>; })()} />,
  );
  if (g.agua.length) piezas.push(
    <Pieza key="a" tema="agua" titulo="Agua gastada"
      dibujo={A.length ? <Cilindros actual={litros} anterior={litrosPrev} color="#2F8FE0" etqActual={periodo} etqAnterior={ANT_CORTO[modo]} unidad="L" alto={120} dec={0} /> : <Gota pct={null} alto={104} />}
      valor={A.length ? `${nf(litros, 0)} L` : "Sin datos"} lineas={[A.length ? (!esDia && diasAgua ? <>media <b style={{ color: C.ink }}>{nf(litros / diasAgua, 0)} L</b> al día</> : null) : "El contador no ha enviado lecturas"]}
      pie={nochesConAgua >= 2 ? <span style={chip(V.bad, V.badClaro)}><Moon size={15} strokeWidth={2.6} />Gasto de madrugada {nochesConAgua} noches</span> : <CambioPill a={litros} b={litrosPrev} antes={antes} />} />,
  );
  if (Dp.length) piezas.push(
    <Pieza key="d" tema="agua" titulo={`Depósito · ${nom(Dp[0])}`} dibujo={<Deposito3D pct={Dp[0].stat.ultimo} alto={124} />} valor={`${nf(Dp[0].stat.ultimo, 0)} %`}
      lineas={[`último dato · media ${nf(Dp[0].stat.media, 0)} %`, `entre ${nf(Dp[0].stat.min, 0)} y ${nf(Dp[0].stat.max, 0)} %`]}
      pie={Dp[0].stat.ultimo <= 10 ? <span style={chip(V.bad, V.badClaro)}><CircleX size={15} strokeWidth={2.6} />Casi vacío</span> : null} />,
  );
  if (Ap.length) piezas.push(
    <Pieza key="ap" tema="aparatos" titulo="Aparatos encendidos" dibujo={<Bombilla on={onTotal > 0} alto={100} />}
      valor={onTotal ? durMin(onTotal) : "0 h"} lineas={[`en total ${periodo}`, Ap.filter((x) => x.on_min > 0).slice(0, 2).map((x) => `${nom(x)} ${durMin(x.on_min)}`).join(" · ") || "ninguno ha estado encendido"]}
      pie={onTotal || onPrevTotal ? <CambioPill a={onTotal} b={onPrevTotal} antes={antes} /> : null} />,
  );

  return (
    <>
      {ahora}
      {controles}
      {ninguno && <div style={{ ...card, padding: 20, fontSize: 14.5, color: C.ink2 }}>Este alojamiento todavía no tiene sensores enviando datos. Cuando se instalen, aquí verás la luz, la temperatura, el agua y los aparatos.</div>}
      {piezas.length > 0 && (
        <div style={{ marginBottom: 30 }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: C.ink, margin: "4px 2px 12px" }}>Así ha ido {periodo} <span style={{ fontSize: 15, fontWeight: 600, color: C.ink2 }}>· comparado con {antes}</span></div>
          <div className="er-diag-tiles" style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : `repeat(${piezas.length === 6 ? 3 : Math.min(piezas.length, 5)}, minmax(0, 1fr))`, gap: isMobile ? 10 : 14 }}>{piezas}</div>
        </div>
      )}

      {g.energia.length > 0 && (
        <Seccion tema="luz" titulo="Luz" sub={`Lo que miden los medidores de electricidad ${periodo}, comparado con ${antes}`}>
          {(kwhDia || hayRelojE) && (
            <div style={{ display: "grid", gridTemplateColumns: dos((!esDia && kwhDia) || (esDia && barrasE) ? 1.6 : 0, hayRelojE ? 1 : 0), gap: 14, marginBottom: 14 }}>
              {!esDia && kwhDia && (
                <TarjetaG titulo="Cada día de un color" ayuda={`Consumo de cada día (${nom(princE)}): cuanto más oscuro, más se ha gastado.`}>
                  <CalendarioColor dias={g.dias} valores={kwhDia} colorDe={colorKwh} texto={(v, largo) => (largo ? `${nf(v, 2)} kWh` : nf(v, v < 10 ? 1 : 0))} hoy={hoy} compacto={isMobile} />
                  <EscalaColor tramos={[[colorKwh(0), "poco"], [colorKwh(maxKwhDia / 2), "medio"], [colorKwh(maxKwhDia), `mucho (${nf(maxKwhDia, 1)} kWh)`]]} />
                  {extremos(g.dias, kwhDia, (v) => `${nf(v, 1)} kWh`, "Día de más gasto", "De menos")}
                </TarjetaG>
              )}
              {esDia && barrasE && (
                <TarjetaG titulo="Consumo por hora (kWh)">
                  <GraficaCategorias tipo="barras" filas={barrasE.filas} unidad="kWh" series={Ek.map((x) => ({ key: "s" + Ek.indexOf(x), label: nom(x), color: Ek.length === 1 ? "#D89400" : colE(x) }))} alto={270} />
                </TarjetaG>
              )}
              {hayRelojE && (
                <TarjetaG titulo={princE ? "¿A qué hora se gasta más luz?" : "¿A qué hora hay más potencia?"}
                  ayuda={esDia ? "Cada porción es una hora de este día. La zona sombreada es la noche." : "Lo que se gasta de media a cada hora en los días del periodo. La zona sombreada es la noche."}>
                  <Reloj24 valores={relojE} color="#E09A00" unidad={princE ? "kWh" : uP} dec={princE ? 2 : 0} etiquetaPico={princE ? "Más gasto" : "Más potencia"} />
                </TarjetaG>
              )}
            </div>
          )}
          {!esDia && (
            <TarjetaG titulo="Consumo por día (kWh)" ayuda={barrasE && Ek.length === 1 ? "La línea discontinua es la media diaria." : undefined} style={{ marginBottom: 14 }}>
              {barrasE ? (
                <>
                  <GraficaCategorias tipo="barras" filas={barrasE.filas} unidad="kWh" refMedia={Ek.length === 1 ? mediaDiaKwh(Ek[0]) : null}
                    series={Ek.map((x) => ({ key: "s" + Ek.indexOf(x), label: nom(x), color: Ek.length === 1 ? "#D89400" : colE(x) }))} />
                  {Ek.length > 1 && <Leyenda items={Ek.map((x) => ({ color: colE(x), label: nom(x) }))} />}
                </>
              ) : <Vacio>Ningún medidor ha registrado consumo {periodo}{E.some((x) => x.potencia_stat && x.potencia_stat.max === 0) ? " (la potencia ha estado a 0 W)" : ""}.</Vacio>}
            </TarjetaG>
          )}
          {esDia && !barrasE && <TarjetaG style={{ marginBottom: 14 }}><Vacio>Ningún medidor ha registrado consumo {periodo}{E.some((x) => x.potencia_stat && x.potencia_stat.max === 0) ? " (la potencia ha estado a 0 W)" : ""}.</Vacio></TarjetaG>}
          <TarjetaG titulo={`Potencia en cada momento (${uP})`} ayuda="Lo que se estaba gastando en cada instante: los picos son aparatos potentes encendidos a la vez." style={{ marginBottom: 14 }}>
            {seriesP.length ? (
              <>
                <GraficaLineas series={seriesP} t0={t0} t1={t1} unidad={uP} limMin={LIM_G.energia} bucketMin={g.bucket_min} />
                {seriesP.length > 1 && <Leyenda items={seriesP.map((s) => ({ color: s.color, label: s.label }))} />}
              </>
            ) : <Vacio>Los medidores no han enviado lecturas {periodo}.</Vacio>}
          </TarjetaG>
          <TarjetaG titulo={`Resumen ${esDia ? "del día" : modo === "semana" ? "de la semana" : "del mes"}`}>
            <TablaResumen cabecera={["Medidor", "Consumo", esDia ? "Potencia media" : "Media al día", "Potencia máxima", FRENTE[modo]]}
              filas={E.map((x) => [
                <span><b style={{ color: C.ink }}>{nom(x)}</b>{x.zona ? <span style={{ color: C.ink3 }}> · {x.zona}</span> : null}</span>,
                fmt(x.kwh, "kWh"),
                esDia ? fmt(x.potencia_stat && x.potencia_stat.media * fP, uP) : fmt(mediaDiaKwh(x), "kWh"),
                x.potencia_stat ? <>{fmt(x.potencia_stat.max * fP, uP)}{x.potencia_stat.max > 0 ? cuando(x.potencia_stat.tmax) : null}</> : "—",
                vsAntes(x.kwh, x.kwh_previo),
              ])} />
          </TarjetaG>
        </Seccion>
      )}

      {g.clima.length > 0 && (
        <Seccion tema="temp" titulo="Temperatura y humedad" sub={`Cómo han estado las estancias ${periodo}. La franja verde es la zona cómoda (${conf.tmin}–${conf.tmax} °C).`}>
          {Cl.length === 0 ? <TarjetaG><Vacio>Los sensores de temperatura no han enviado lecturas {periodo}.</Vacio></TarjetaG> : (
            <>
              <div style={{ display: "grid", gridTemplateColumns: dos(Cl.length > 1 ? 1.6 : 1.25, hayRelojT ? 1 : 0), gap: 14, marginBottom: 14 }}>
                <div style={{ display: "grid", gridTemplateColumns: Cl.length > 1 && !isMobile ? "1fr 1fr" : "1fr", gap: 14 }}>
                  {Cl.map((x, i) => (
                    <div key={x.device} onClick={() => onOpen(x.device)} style={{ ...card, padding: 16, cursor: "pointer", borderTop: `6px solid ${colCl(i)}`, boxShadow: "0 4px 16px rgba(20,30,15,0.06)", minWidth: 0 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                        <div style={{ fontWeight: 800, color: C.ink, fontSize: 18 }}>{nom(x)}</div>
                        <div style={{ fontSize: 12.5, color: C.ink3 }}>{sub(x)}</div>
                      </div>
                      <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 8 }}>
                        <Termometro3D valor={x.temp.media} tmin={conf.tmin} tmax={conf.tmax} alto={138} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 34, fontWeight: 800, color: C.ink, lineHeight: 1 }}>{nf(x.temp.media, 1)} °C</div>
                          <div style={{ fontSize: 13, color: C.ink2, marginTop: 4 }}>de media {periodo}</div>
                          <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                            <MiniDato icon={Snowflake} color={V.frio} l="mín." v={`${nf(x.temp.min, 1)} °C`} />
                            <MiniDato icon={Flame} color={V.calor} l="máx." v={`${nf(x.temp.max, 1)} °C`} />
                          </div>
                        </div>
                        {x.hum && (
                          <div style={{ textAlign: "center", flexShrink: 0 }}>
                            <Gota pct={x.hum.media} alto={82} />
                            <div style={{ fontSize: 12, color: C.ink2, fontWeight: 700, marginTop: 2 }}>humedad</div>
                          </div>
                        )}
                      </div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
                        {x.confort && <ChipConfort pct={x.confort.temp_pct} />}
                        {x.temp_previo && <span style={chip(C.ink2, "#EEF0EB")}>{difTexto(x.temp.media, x.temp_previo.media, "°C")}</span>}
                      </div>
                      {x.hum && (
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.line}`, alignItems: "center" }}>
                          <span style={{ fontSize: 13, fontWeight: 800, color: V.aguaOsc, marginRight: 2 }}>Humedad</span>
                          <MiniDato icon={ArrowDown} color={V.frio} l="mín." v={`${nf(x.hum.min, 0)} %`} />
                          <MiniDato icon={ArrowUp} color={V.calor} l="máx." v={`${nf(x.hum.max, 0)} %`} />
                          {x.confort && esNum(x.confort.hum_pct) && <MiniDato icon={x.confort.hum_pct >= 80 ? Smile : Meh} color={x.confort.hum_pct >= 80 ? V.ok : V.warn} l="correcta el" v={`${nf(x.confort.hum_pct, 0)} % del tiempo`} />}
                          {x.hum_previo && <span style={{ fontSize: 12.5, color: C.ink2 }}>{difTexto(x.hum.media, x.hum_previo.media, "puntos")}</span>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {hayRelojT && (
                  <TarjetaG titulo="¿A qué hora hace más frío o más calor?" ayuda={esDia ? "Temperatura de cada hora de este día." : "Temperatura media de cada hora en los días del periodo."}>
                    <Reloj24 modo="anillo" valores={relojT} colorDe={(v) => colorTemp(v, conf)} unidad="°C" dec={1} etiquetaPico="Más calor" />
                    {frioH !== null && <div style={{ textAlign: "center", fontSize: 13.5, color: C.ink2, marginTop: 6 }}>Más frío de {frioH} a {frioH + 1} h: <b style={{ color: V.frioOsc }}>{nf(relojT[frioH], 1)} °C</b></div>}
                  </TarjetaG>
                )}
              </div>
              {!esDia ? (
                <TarjetaG style={{ marginBottom: 14 }} titulo="Cada día de un color" ayuda="Temperatura media de cada día: azul si hizo frío, verde si fue cómoda y naranja si hizo calor.">
                  <CalendarioColor dias={g.dias} valores={tDia} colorDe={(v) => colorTemp(v, conf)} texto={(v, largo) => (largo ? `${nf(v, 1)} °C de media` : `${nf(v, 1)}°`)} hoy={hoy} compacto={isMobile} />
                  <EscalaColor tramos={[[colorTemp(conf.tmin - 4, conf), "frío"], [colorTemp(conf.tmin, conf), `cómoda (${conf.tmin}–${conf.tmax} °C)`], [colorTemp(conf.tmax + 4, conf), "calor"]]} />
                  {extremos(g.dias, tDia, (v) => `${nf(v, 1)} °C`, "Día más caluroso", "Más frío")}
                </TarjetaG>
              ) : (
                <TarjetaG style={{ marginBottom: 14 }} titulo="Temperatura a lo largo del día (°C)">
                  <GraficaLineas series={Cl.map((x, i) => ({ key: "s" + i, label: nom(x), color: colCl(i), puntos: x.temperatura }))} t0={t0} t1={t1} unidad="°C"
                    limMin={LIM_G.clima} bucketMin={g.bucket_min} banda={[conf.tmin, conf.tmax]} alto={280} />
                  {Cl.length > 1 && <Leyenda items={Cl.map((x, i) => ({ color: colCl(i), label: nom(x) }))} />}
                </TarjetaG>
              )}
              {!esDia && (
                <TarjetaG titulo="Temperatura (°C)" ayuda="La franja verde es la zona cómoda." style={{ marginBottom: 14 }}>
                  <GraficaLineas series={Cl.map((x, i) => ({ key: "s" + i, label: nom(x), color: colCl(i), puntos: x.temperatura }))} t0={t0} t1={t1} unidad="°C"
                    limMin={LIM_G.clima} bucketMin={g.bucket_min} banda={[conf.tmin, conf.tmax]} />
                  <Leyenda items={[...(Cl.length > 1 ? Cl.map((x, i) => ({ color: colCl(i), label: nom(x) })) : []), { color: "rgba(58,125,28,0.35)", label: "Zona cómoda", extra: ` · de ${conf.tmin} a ${conf.tmax} °C` }]} />
                </TarjetaG>
              )}
              <div style={{ display: "grid", gridTemplateColumns: esDia ? "1fr" : dos(1, 1), gap: 14, marginBottom: 14 }}>
                {!esDia && (
                  <TarjetaG titulo="Cada día: media, mínima y máxima (°C)" ayuda={Cl.length === 1 ? "La sombra va de la mínima a la máxima de cada día." : undefined}>
                    <GraficaCategorias unidad="°C" banda={[conf.tmin, conf.tmax]}
                      filas={g.dias.map((dia, j) => { const r = { etq: etqDia(dia) }; Cl.forEach((x, i) => { const v = x.temp_dia[j]; r["m" + i] = v ? v.media : null; if (i === 0) r.b = v ? [v.min, v.max] : null; }); return r; })}
                      series={[...(Cl.length === 1 ? [{ key: "b", label: `${nom(Cl[0])} · mín. y máx.`, color: colCl(0), banda: true }] : []), ...Cl.map((x, i) => ({ key: "m" + i, label: `${nom(x)} · media`, color: colCl(i) }))]} />
                  </TarjetaG>
                )}
                {Cl.some((x) => x.humedad.length) && (
                  <TarjetaG titulo="Humedad (%)" ayuda={`Lo cómodo es de ${conf.hmin} a ${conf.hmax} % (franja verde).`}>
                    <GraficaLineas series={Cl.filter((x) => x.humedad.length).map((x) => ({ key: "s" + Cl.indexOf(x), label: nom(x), color: Cl.length === 1 ? V.agua : colCl(Cl.indexOf(x)), puntos: x.humedad }))}
                      t0={t0} t1={t1} unidad="%" limMin={LIM_G.clima} bucketMin={g.bucket_min} banda={[conf.hmin, conf.hmax]} alto={240} />
                  </TarjetaG>
                )}
              </div>
              <TarjetaG titulo={`Resumen ${esDia ? "del día" : modo === "semana" ? "de la semana" : "del mes"}`}>
                <TablaResumen cabecera={["Estancia", "Temp. media", "Mínima", "Máxima", "Cómoda", "Humedad media", FRENTE[modo]]}
                  filas={Cl.map((x) => [
                    <b style={{ color: C.ink }}>{nom(x)}</b>,
                    fmt(x.temp.media, "°C"),
                    <>{fmt(x.temp.min, "°C")}{cuando(x.temp.tmin)}</>,
                    <>{fmt(x.temp.max, "°C")}{cuando(x.temp.tmax)}</>,
                    x.confort ? `${nf(x.confort.temp_pct, 0)} % del tiempo` : "—",
                    x.hum ? fmt(x.hum.media, "%") : "—",
                    x.temp_previo ? difAbs(x.temp.media, x.temp_previo.media, "°C") : "—",
                  ])} />
              </TarjetaG>
            </>
          )}
        </Seccion>
      )}

      {(g.agua.length > 0 || g.deposito.length > 0) && (
        <Seccion tema="agua" titulo="Agua" sub={`Agua gastada y nivel de los depósitos ${periodo}`}>
          {nochesConAgua >= 2 && (
            <div style={{ display: "flex", gap: 14, alignItems: "center", padding: "14px 18px", borderRadius: 16, background: V.badClaro, border: `2px solid ${V.bad}`, marginBottom: 14 }}>
              <span style={{ width: 44, height: 44, borderRadius: 14, background: V.bad, color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Moon size={22} strokeWidth={2.3} /></span>
              <div style={{ fontSize: 14.5, color: C.ink, lineHeight: 1.45 }}>
                <b style={{ fontSize: 16 }}>Se ha gastado agua de madrugada {nochesConAgua} noches.</b><br />Entre las 2 y las 5 h casi nadie usa agua: si en el alojamiento no había nadie despierto, puede haber una fuga (cisterna, grifo o tubería).
              </div>
            </div>
          )}
          {Dp.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: dos(1, 2.2), gap: 14, marginBottom: 14 }}>
              {Dp.map((x) => (
                <TarjetaG key={x.device} titulo={`Depósito · ${nom(x)}`}>
                  <div style={{ display: "flex", gap: 16, alignItems: "center" }} onClick={() => onOpen(x.device)}>
                    <Deposito3D pct={x.stat.ultimo} alto={160} />
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      <div style={{ fontSize: 13, color: C.ink2 }}>último dato</div>
                      <div style={{ fontSize: 32, fontWeight: 800, color: x.stat.ultimo <= 10 ? V.bad : C.ink, lineHeight: 1 }}>{nf(x.stat.ultimo, 0)} %</div>
                      <MiniDato icon={Gauge} color={V.agua} l="media" v={`${nf(x.stat.media, 0)} %`} />
                      <MiniDato icon={ArrowDown} color={V.frio} l="mín." v={`${nf(x.stat.min, 0)} %`} />
                      <MiniDato icon={ArrowUp} color={V.calor} l="máx." v={`${nf(x.stat.max, 0)} %`} />
                      {x.stat_previo && <div style={{ fontSize: 12.5, color: C.ink2 }}>media {antes}: <b style={{ color: C.ink }}>{nf(x.stat_previo.media, 0)} %</b></div>}
                    </div>
                  </div>
                </TarjetaG>
              ))}
              <TarjetaG titulo="Nivel del depósito (%)">
                <GraficaLineas series={Dp.map((x, i) => ({ key: "s" + i, label: nom(x), color: Dp.length === 1 ? V.agua : colA(i), puntos: x.nivel }))} t0={t0} t1={t1} unidad="%"
                  limMin={LIM_G.nivel} bucketMin={g.bucket_min} dominio={[0, 100]} alto={220} />
              </TarjetaG>
            </div>
          )}
          {g.deposito.length > 0 && !Dp.length && <TarjetaG style={{ marginBottom: 14 }}><Vacio>El sensor del depósito no ha enviado lecturas {periodo}.</Vacio></TarjetaG>}
          {g.agua.length > 0 && (barrasA ? (
            <>
              <div style={{ display: "grid", gridTemplateColumns: dos(1.6, relojA ? 1 : 0), gap: 14, marginBottom: 14 }}>
                {!esDia ? (
                  <TarjetaG titulo="Cada día de un color" ayuda="Litros gastados cada día: cuanto más oscuro, más agua.">
                    <CalendarioColor dias={g.dias} valores={litrosDia} colorDe={(v) => mezcla("#E4F1FD", V.aguaOsc, v / maxLitrosDia)} texto={(v, largo) => (largo ? `${nf(v, 0)} litros` : nf(v, 0))} hoy={hoy} compacto={isMobile} />
                    <EscalaColor tramos={[["#E4F1FD", "poco"], [mezcla("#E4F1FD", V.aguaOsc, 0.5), "medio"], [V.aguaOsc, `mucho (${nf(maxLitrosDia, 0)} L)`]]} />
                    {extremos(g.dias, litrosDia, (v) => `${nf(v, 0)} L`, "Día de más gasto", "De menos")}
                  </TarjetaG>
                ) : (
                  <TarjetaG titulo="Agua gastada por hora (litros)">
                    <GraficaCategorias tipo="barras" filas={barrasA.filas} unidad="L" alto={270}
                      series={A.map((x, i) => ({ key: "s" + i, label: nom(x), color: A.length === 1 ? "#2F8FE0" : colA(i) }))} />
                    <div style={{ fontSize: 14, color: C.ink2, marginTop: 10 }}>Total: <b style={{ color: C.ink }}>{nf(litros, 0)} litros</b> {periodo} · {vsAntes(litros, litrosPrev)}</div>
                  </TarjetaG>
                )}
                {relojA && (
                  <TarjetaG titulo="¿A qué hora se gasta agua?" ayuda={esDia ? "Litros de cada hora de este día. La zona sombreada es la noche." : "Litros de media a cada hora. La zona sombreada es la noche."}>
                    <Reloj24 valores={relojA} color="#2F8FE0" unidad="L" dec={0} />
                  </TarjetaG>
                )}
              </div>
              {!esDia && (
                <TarjetaG titulo="Agua gastada por día (litros)" ayuda={diasAgua ? "La línea discontinua es la media diaria." : undefined} style={{ marginBottom: 14 }}>
                  <GraficaCategorias tipo="barras" filas={barrasA.filas} unidad="L" refMedia={diasAgua ? litros / diasAgua : null}
                    series={A.map((x, i) => ({ key: "s" + i, label: nom(x), color: A.length === 1 ? "#2F8FE0" : colA(i) }))} />
                  <div style={{ fontSize: 14, color: C.ink2, marginTop: 10, lineHeight: 1.6 }}>
                    Total: <b style={{ color: C.ink }}>{nf(litros, 0)} litros</b> {periodo}{diasAgua ? ` · ${nf(litros / diasAgua, 0)} L al día de media` : ""} · {vsAntes(litros, litrosPrev)}
                  </div>
                </TarjetaG>
              )}
            </>
          ) : <TarjetaG style={{ marginBottom: 14 }}><Vacio>El contador de agua no ha registrado consumo {periodo}{A.length === 0 ? " (no ha enviado lecturas)" : ""}.</Vacio></TarjetaG>)}
        </Seccion>
      )}

      {Ap.length > 0 && (
        <Seccion tema="aparatos" titulo="Aparatos" sub={`Lo que se puede encender y apagar desde la app, y cuánto ha estado encendido ${periodo}`}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 270px), 1fr))", gap: 12, marginBottom: barrasAp ? 14 : 0 }}>
            {Ap.map((x, i) => {
              const on = x.estado === "ON", valv = esValv(x);
              const viejo = x.ultimo && Date.now() - Date.parse(x.ultimo) > 26 * HORA;
              const etq = !x.estado ? "SIN DATOS" : valv ? (on ? "ABIERTA" : "CERRADA") : on ? "ENCENDIDO" : "APAGADO";
              const [cc, bg] = !x.estado ? [C.ink2, "#EEF0EB"] : on ? (valv ? [V.aguaOsc, V.aguaClaro] : [V.luzOsc, V.luzClaro]) : valv ? [V.bad, V.badClaro] : [C.ink, "#E3E6DF"];
              return (
                <div key={x.device} onClick={() => onOpen(x.device)} style={{ ...card, padding: 14, cursor: "pointer", display: "flex", gap: 14, alignItems: "center", borderLeft: `6px solid ${COL_EQ[(i + 3) % COL_EQ.length]}`, boxShadow: "0 4px 16px rgba(20,30,15,0.06)", minWidth: 0 }}>
                  <div style={{ width: 80, display: "flex", justifyContent: "center", flexShrink: 0 }}><DibujoAparato nombre={`${x.device} ${nom(x)}`} tipo={x.tipo} on={on} alto={78} /></div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 800, fontSize: 17, color: C.ink, overflow: "hidden", textOverflow: "ellipsis" }}>{nom(x)}</div>
                    <span style={{ display: "inline-block", marginTop: 5, padding: "3px 11px", borderRadius: 999, background: bg, color: cc, fontWeight: 800, fontSize: 12.5, letterSpacing: 0.6 }}>{etq}</span>
                    {viejo && <div style={{ fontSize: 12.5, color: V.warn, fontWeight: 700, marginTop: 4 }}>último estado {hace((Date.now() - Date.parse(x.ultimo)) / 60000)}</div>}
                    <div style={{ fontSize: 13.5, color: C.ink2, marginTop: 6, lineHeight: 1.4 }}>
                      <b style={{ color: C.ink }}>{x.on_min ? durMin(x.on_min) : "0 min"}</b> {valv ? "abierta" : "encendido"} {periodo}
                      {esNum(x.on_min_previo) && (x.on_min || x.on_min_previo) ? <span> · {antes}: {durMin(x.on_min_previo)}</span> : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {barrasAp && (
            <TarjetaG titulo="Horas encendido cada día">
              <GraficaCategorias tipo="barras" filas={barrasAp} unidad="h" alto={220}
                series={Ap.map((x, i) => ({ key: "s" + i, label: nom(x), color: COL_EQ[(i + 3) % COL_EQ.length] }))} />
              {Ap.length > 1 && <Leyenda items={Ap.map((x, i) => ({ color: COL_EQ[(i + 3) % COL_EQ.length], label: nom(x) }))} />}
            </TarjetaG>
          )}
        </Seccion>
      )}

      {d && d.equipos.length > 0 && (
        <Seccion tema="datos" titulo="¿Llegan bien los datos?" sub="Cada raya es una hora de las últimas 48: verde si ha llegado algo del equipo">
          <div style={{ ...card, padding: "6px 16px" }}>
            {d.equipos.filter((e) => e.archivado).map((e, i) => {
              const caido = e.estado === "caido";
              return (
                <div key={e.device} onClick={() => onOpen(e.device)}
                  style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "minmax(160px, 1fr) auto 150px", gap: isMobile ? 6 : 16, alignItems: "center", padding: "10px 0", borderTop: i ? `1px solid ${C.line}` : "none", cursor: "pointer" }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 9, minWidth: 0 }}>
                    {caido ? <CircleX size={17} strokeWidth={2.6} color={V.bad} /> : <CircleCheck size={17} strokeWidth={2.6} color={V.ok} />}
                    <b style={{ color: C.ink, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{nombreDe(e.device)}</b>
                  </span>
                  <Actividad48 horas={e.por_hora_48} limite={e.limite_min} />
                  <span style={{ fontSize: 13, fontWeight: caido ? 800 : 600, color: caido ? V.bad : C.ink2, textAlign: isMobile ? "left" : "right" }}>{caido ? "sin datos · " : ""}{hace(e.min_sin_datos)}</span>
                </div>
              );
            })}
          </div>
        </Seccion>
      )}
    </>
  );
}

function Resumen({ d, dias, info, nombreDe, onOpen, isMobile }) {
  const r = d.resumen;
  const importantes = r.avisos.bad, revisar = r.avisos.warn;
  const grupos = GRUPOS.map((g) => ({
    g,
    eqs: d.equipos.filter((e) => (TIPO[e.tipo] || TIPO.desconocido).grupo === g)
      .sort((a, b) => ["bad", "warn", "off", "good"].indexOf(toneEq(a)) - ["bad", "warn", "off", "good"].indexOf(toneEq(b)) || a.device.localeCompare(b.device)),
  })).filter((x) => x.eqs.length);
  const periodo = dias === 1 ? "hoy" : `últimos ${dias} días`;
  const cols = "minmax(190px, 2fr) 120px 100px 96px 200px 92px 64px minmax(130px, 1.3fr) 16px";
  return (
    <>
      <div className="er-diag-tiles" style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: isMobile ? 10 : 14, marginBottom: 18 }}>
        <Cifra label="Equipos enviando" valor={`${r.ok}/${r.equipos}`} color={r.ok === r.equipos ? C.moss : C.ochre}
          sub={r.caidos || r.sin_datos ? [r.caidos && `${r.caidos} se han callado`, r.sin_datos && `${r.sin_datos} sin estrenar`].filter(Boolean).join(" · ") : "todos envían"} />
        <Cifra label={`Disponibilidad · ${periodo}`} valor={esNum(r.disponibilidad) ? `${nf(r.disponibilidad, 1)} %` : "—"}
          color={!esNum(r.disponibilidad) ? C.ink4 : r.disponibilidad >= 95 ? C.moss : r.disponibilidad >= 80 ? C.ochre : C.clay}
          sub="tiempo con datos, media de los equipos" />
        <Cifra label="Lecturas guardadas · 24 h" valor={nf(r.mensajes_24h, 0)} color={C.sky} sub={`${nf(r.mensajes, 0)} en ${periodo}`} />
        <Cifra label="Avisos" valor={importantes + revisar} color={importantes ? C.clay : revisar ? C.ochre : C.moss}
          sub={importantes ? `${importantes} importante${importantes === 1 ? "" : "s"}${revisar ? ` · ${revisar} para revisar` : ""}` : revisar ? "nada grave" : "todo en orden"} />
      </div>

      <Tarjeta titulo="Lo importante ahora" style={{ marginBottom: 18 }}>
        {d.avisos.length === 0
          ? <div style={{ fontSize: 14, color: C.ink2 }}>Todo en orden: los equipos envían con normalidad y lo que miden está dentro de lo esperable.</div>
          : <Avisos lista={d.avisos} nombreDe={nombreDe} onOpen={onOpen} max={6} />}
      </Tarjeta>

      {grupos.map(({ g, eqs }) => (
        <div key={g} style={{ marginBottom: 18 }}>
          <Eyebrow style={{ margin: "0 0 8px 4px" }}>{g} · {eqs.length}</Eyebrow>
          <div style={{ ...card, overflow: "hidden" }}>
            {!isMobile && (
              <div style={{ display: "grid", gridTemplateColumns: cols, gap: 12, padding: "10px 16px", borderBottom: `1px solid ${C.line}`, background: "#FAFAFA" }}>
                {["Equipo", "Último dato", "Frecuencia", "Por día", "Últimas 48 h", "Disponib.", "Batería", "Ahora", ""].map((h, i) => (
                  <div key={i} style={{ ...mono, fontSize: 10, letterSpacing: 0.9, textTransform: "uppercase", color: C.ink3 }}>{h}</div>
                ))}
              </div>
            )}
            {eqs.map((e, i) => (
              <FilaEquipo key={e.device} e={e} info={info[e.device]} dias={d.dias} cols={cols} primera={i === 0} onOpen={onOpen} isMobile={isMobile} />
            ))}
          </div>
        </div>
      ))}
      <div style={{ fontSize: 12.5, color: C.ink3, margin: "-4px 4px 22px", lineHeight: 1.6 }}>
        «Por día» son las lecturas guardadas cada día (la barra oscura es hoy). En «Últimas 48 h» cada raya es una hora: verde si llegó algo,
        rojiza si no llegó nada en un equipo que debería hablar cada hora. Pulsa un equipo para ver sus gráficas.
      </div>
    </>
  );
}

function FilaEquipo({ e, info, dias, cols, primera, onOpen, isMobile }) {
  const tone = toneEq(e);
  const T = TIPO[e.tipo] || TIPO.desconocido;
  const Icon = T.icon;
  const nombre = (info && info.alias) || e.device;
  const sub = [info && info.alias ? e.device : null, info && info.modelo_nombre, info && info.zona].filter(Boolean).join(" · ") || T.label;
  const f = frecuencia(e);
  const base = { borderTop: primera ? "none" : `1px solid ${C.line}`, cursor: "pointer", background: "#fff" };
  const cab = (
    <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
      <Mark tone={tone} size={10} />
      <Icon size={16} strokeWidth={1.7} color={C.ink3} style={{ flexShrink: 0 }} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 700, color: C.ink, fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{nombre}</div>
        <div style={{ fontSize: 12, color: C.ink3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{sub}</div>
      </div>
    </div>
  );
  if (isMobile) {
    return (
      <div onClick={() => onOpen(e.device)} style={{ ...base, padding: "14px 16px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
          {cab}
          <ChevronRight size={16} color={C.ink4} />
        </div>
        <div style={{ fontSize: 12.5, color: C.ink2, margin: "8px 0 8px 20px", lineHeight: 1.6 }}>
          {hace(e.min_sin_datos)} · {cada(f)} · disponible <Disp v={e.disponibilidad} />{e.bateria && esNum(e.bateria.ultima) ? <> · batería <Bateria b={e.bateria} /></> : null}
          <div style={{ color: C.ink, fontWeight: 600 }}>{valorActual(e)}</div>
        </div>
        {e.archivado && <div style={{ marginLeft: 20 }}><Actividad48 horas={e.por_hora_48} limite={e.limite_min} /></div>}
      </div>
    );
  }
  return (
    <div onClick={() => onOpen(e.device)} className="er-hoverline"
      style={{ ...base, display: "grid", gridTemplateColumns: cols, gap: 12, padding: "12px 16px", alignItems: "center", fontSize: 13.5, color: C.ink2 }}>
      {cab}
      <div title={e.ultimo ? fechaHora(e.ultimo) : undefined} style={{ color: e.estado === "caido" ? C.clay : C.ink2, fontWeight: e.estado === "caido" ? 600 : 400 }}>{hace(e.min_sin_datos)}</div>
      <div>{cada(f)}</div>
      {e.archivado ? <MiniBarras valores={e.por_dia} dias={dias} /> : <span style={{ color: C.ink4 }}>—</span>}
      {e.archivado ? <Actividad48 horas={e.por_hora_48} limite={e.limite_min} /> : <span style={{ fontSize: 12, color: C.ink4 }}>{e.tipo === "pasarela" ? "solo estado, sin historial" : "sin historial"}</span>}
      <Disp v={e.disponibilidad} />
      <Bateria b={e.bateria} />
      <div style={{ color: C.ink, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{valorActual(e)}</div>
      <ChevronRight size={16} color={C.ink4} />
    </div>
  );
}

function ServiciosExternos({ acc, isMobile }) {
  const { data: sum } = useApi(() => api.getConsumptionSummary(acc.locationID), [acc.locationID]);
  const { data: meteo } = useApi(() => api.getMeteo(acc.locationID), [acc.locationID]);
  const { data: precios } = useApi(() => api.getPrecios(), []);
  const { data: alarmas } = useApi(() => api.getAlarms(acc.locationID, 7), [acc.locationID]);
  const filas = [
    {
      icon: BarChart3, label: "Consumo eléctrico (Datadis)",
      tone: sum ? (sum.latest_day ? "good" : "off") : null,
      det: sum ? (sum.latest_day ? `Datos hasta el ${diaCorto(String(sum.latest_day).slice(0, 10))} · ${sum.days_available} días guardados` : "Sin datos de la distribuidora para este alojamiento") : "…",
    },
    {
      icon: CloudSun, label: "Meteorología",
      tone: meteo ? ((meteo.days || []).length ? "good" : "warn") : null,
      det: meteo ? ((meteo.days || []).length ? `Previsión de ${meteo.days.length} días · ${meteo.fuente === "open-meteo" ? "Open-Meteo" : "AEMET"}` : "No ha llegado la previsión") : "…",
    },
    {
      icon: Euro, label: "Precio de la luz (REE)",
      tone: precios ? (precios.count >= 24 ? "good" : "warn") : null,
      det: precios ? (precios.count >= 24 ? "Precio de hoy por horas" : `Solo ${precios.count || 0} horas de hoy`) : "…",
    },
    {
      icon: Bell, label: "Avisos enviados",
      tone: alarmas ? "info" : null,
      det: alarmas ? `${alarmas.count} en los últimos 7 días` : "…",
    },
  ];
  return (
    <div style={{ marginTop: 8 }}>
      <Eyebrow style={{ margin: "0 0 8px 4px" }}>Servicios de datos externos</Eyebrow>
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: 12 }}>
        {filas.map((f) => (
          <div key={f.label} style={{ ...card, padding: "14px 16px", display: "flex", gap: 12, alignItems: "center" }}>
            <f.icon size={18} strokeWidth={1.7} color={C.primary} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, color: C.ink, fontSize: 14 }}>{f.label}</div>
              <div style={{ fontSize: 12.5, color: C.ink3 }}>{f.det}</div>
            </div>
            {f.tone && <Mark tone={f.tone} size={10} />}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Ficha de un equipo ──────────────────────────────────────────────────────
const RANGOS = [[{ horas: 24 }, "24 h"], [{ horas: 72 }, "3 días"], [{ horas: 168 }, "7 días"], [{ horas: 720 }, "30 días"]];
const DEFECTO = {
  energia_trifasica: ["power", "voltage_a", "voltage_b", "voltage_c", "current_a", "current_b", "current_c"],
  energia_monofasica: ["power", "voltage", "current"],
  clima: ["temperature", "humidity"],
  nivel_agua: ["liquid_level_percent", "liquid_depth"],
  pulsos_agua: ["temperature"],
  actuador: ["power", "brightness", "battery"],
  pasarela: ["modbus_ok"],
};

function Ficha({ acc, eq, info, todos, nombreDe, onBack, isMobile }) {
  const [rango, setRango] = useState({ horas: 24 });
  const [desdeIn, setDesdeIn] = useState("");
  const [hastaIn, setHastaIn] = useState("");
  const [elegidosUser, setElegidos] = useState(null);
  const [comparar, setComparar] = useState("");
  const rk = JSON.stringify(rango);
  const { data, loading, error, refetch } = useApi(() => api.getDiagnosticoEquipo(acc.locationID, eq.device, rango), [acc.locationID, eq.device, rk]);
  const d = data && data.device === eq.device ? data : null;
  const T = TIPO[eq.tipo] || TIPO.desconocido;
  const Icon = T.icon;

  const campos = d ? d.campos : [];
  const graficables = campos.filter((x) => d.serie[x.campo] && !x.contador);
  const contadores = campos.filter((x) => x.contador && ((x.total || 0) > 0 || x.variable || (x.previo && (x.previo.total || 0) > 0)));
  const fijos = campos.filter((x) => !x.variable && !x.contador && x.campo !== "battery" && x.campo !== "linkquality");
  const disponibles = graficables.map((x) => x.campo);
  const defecto = (DEFECTO[eq.tipo] || []).filter((k) => disponibles.includes(k));
  const elegidos = (elegidosUser || (defecto.length ? defecto : disponibles.slice(0, 2))).filter((k) => disponibles.includes(k));
  const toggle = (k) => setElegidos(elegidos.includes(k) ? elegidos.filter((x) => x !== k) : [...elegidos, k]);

  const t0 = d ? Date.parse(d.desde) : 0, t1 = d ? Date.parse(d.hasta) : 0, span = t1 - t0;
  const cmpInfo = useMemo(() => {
    if (!d || !comparar) return null;
    if (comparar === "previo") return { device: eq.device, t0: t0 - span, t1: t0, shift: span, etiqueta: "Periodo anterior" };
    if (comparar === "semana") return { device: eq.device, t0: t0 - 7 * DIA, t1: t1 - 7 * DIA, shift: 7 * DIA, etiqueta: "Hace una semana" };
    if (comparar.startsWith("eq:")) return { device: comparar.slice(3), t0, t1, shift: 0, etiqueta: nombreDe(comparar.slice(3)) };
    return null;
  }, [d, comparar, t0, t1, span, eq.device, nombreDe]);
  const ck = cmpInfo ? `${cmpInfo.device}|${cmpInfo.t0}|${cmpInfo.t1}|${elegidos.join(",")}` : "";
  const { data: cmp } = useApi(
    () => (cmpInfo ? api.getDiagnosticoEquipo(acc.locationID, cmpInfo.device, { t0: cmpInfo.t0, t1: cmpInfo.t1, campos: elegidos }) : Promise.resolve(null)),
    [ck]
  );
  const cmpOk = cmp && cmpInfo && cmp.device === cmpInfo.device ? cmp : null;
  const candidatos = todos.filter((x) => x.device !== eq.device && (x.campos || []).some((c) => elegidos.includes(c.campo)));

  // Gráficas: una por unidad (las tres fases de la tensión van juntas)
  const grupos = [];
  elegidos.forEach((k) => {
    const c = campos.find((x) => x.campo === k);
    const [, u] = etiqueta(k, eq.tipo);
    const clave = u + (c && c.bool ? "·b" : "");
    let g = grupos.find((x) => x.clave === clave);
    if (!g) { g = { clave, unidad: u, bool: !!(c && c.bool), campos: [] }; grupos.push(g); }
    g.campos.push(k);
  });

  const aplicarFechas = () => {
    if (!desdeIn) return;
    setRango({ desde: desdeIn, hasta: hastaIn && hastaIn >= desdeIn ? hastaIn : desdeIn });
  };
  const descargar = async () => {
    try {
      const dias = Math.min(400, Math.max(1, Math.ceil((Date.now() - (t0 || Date.now())) / DIA) + 1));
      const csv = await api.getSensorCsv(acc.locationID, eq.device, dias);
      const blob = new Blob([typeof csv === "string" ? csv : JSON.stringify(csv)], { type: "text/csv;charset=utf-8" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `easyrural_${acc.locationID}_${eq.device}.csv`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    } catch (e) { window.alert("No se ha podido descargar: " + e.message); }
  };
  const rangoTxt = d ? `${fechaHora(t0)} → ${fechaHora(Math.min(t1, Date.now()))}` : "";
  const f = frecuencia(eq);
  const msgPrevio = d ? d.previo.mensajes : null;

  return (
    <div>
      <button style={{ ...btn, marginBottom: 16 }} onClick={onBack}><ArrowLeft size={15} strokeWidth={1.8} />Todos los equipos</button>

      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap", alignItems: "flex-start", marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 14, alignItems: "center", minWidth: 0 }}>
          <span style={{ width: 48, height: 48, borderRadius: 14, background: C.light, color: C.primary, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Icon size={22} strokeWidth={1.6} />
          </span>
          <div style={{ minWidth: 0 }}>
            <h2 style={{ fontSize: isMobile ? 24 : 30, fontWeight: 700, color: C.primary, margin: 0, fontFamily: "var(--font-serif)", lineHeight: 1.15 }}>{(info && info.alias) || eq.device}</h2>
            <div style={{ fontSize: 13.5, color: C.ink3, marginTop: 2 }}>
              {[info && info.alias ? eq.device : null, (info && info.modelo_nombre) || T.label, info && info.zona].filter(Boolean).join(" · ")}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Mark tone={toneEq(eq)} size={10} />
          <span style={{ ...mono, fontSize: 11, letterSpacing: 0.8, textTransform: "uppercase", color: C.ink2 }}>{TONE_TXT[toneEq(eq)]}</span>
        </div>
      </div>

      <div className="er-diag-tiles" style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(6, 1fr)", gap: isMobile ? 10 : 12, marginBottom: 16 }}>
        <Cifra label="Último dato" valor={hace(eq.min_sin_datos)} sub={eq.ultimo ? fechaHora(eq.ultimo) : "nunca ha enviado"} color={eq.estado === "ok" ? C.moss : C.clay} />
        <Cifra label="Frecuencia" valor={cada(f).replace("cada ", "")} sub="entre lecturas, de media" color={C.sky} />
        <Cifra label="Disponibilidad" valor={d && esNum(d.disponibilidad) ? `${nf(d.disponibilidad, 1)} %` : "—"} sub="en el rango elegido"
          color={!d || !esNum(d.disponibilidad) ? C.ink4 : d.disponibilidad >= 95 ? C.moss : d.disponibilidad >= 80 ? C.ochre : C.clay} />
        <Cifra label="Lecturas" valor={d ? nf(d.mensajes, 0) : "…"} color={C.primary}
          sub={d && esNum(msgPrevio) ? (msgPrevio ? `${d.mensajes >= msgPrevio ? "+" : ""}${nf(((d.mensajes - msgPrevio) / msgPrevio) * 100, 0)} % que el periodo anterior` : "el periodo anterior no tenía") : ""} />
        <Cifra label="Batería" valor={eq.bateria && esNum(eq.bateria.ultima) ? `${nf(eq.bateria.ultima, 0)} %` : "—"}
          sub={eq.bateria ? (eq.bateria.dias_restantes ? `unos ${eq.bateria.dias_restantes} días a este ritmo` : esNum(eq.bateria.caida_dia) && eq.bateria.caida_dia <= 0.2 ? "estable" : "") : "va enchufado"}
          color={eq.bateria && esNum(eq.bateria.ultima) ? (eq.bateria.ultima <= 15 ? C.clay : eq.bateria.ultima <= 30 ? C.ochre : C.moss) : C.ink4} />
        <Cifra label="Señal Zigbee" valor={eq.senal && eq.senal.ultima ? nf(eq.senal.ultima, 0) : "—"}
          sub={!eq.senal ? "no aplica" : !eq.senal.ultima && !eq.senal.media ? "zigbee2mqtt no la informa" : `media ${nf(eq.senal.media, 0)} · mín. ${nf(eq.senal.min, 0)} (de 255)`} color={C.ink4} />
      </div>

      {eq.avisos.length > 0 && (
        <Tarjeta titulo="Avisos de este equipo" style={{ marginBottom: 16 }}>
          <Avisos lista={eq.avisos} nombreDe={nombreDe} />
        </Tarjeta>
      )}

      <div style={{ ...card, padding: 14, marginBottom: 16, display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <Eyebrow style={{ width: 86 }}>Ver</Eyebrow>
          {RANGOS.map(([r, l]) => <button key={l} style={pill(rango.horas === r.horas && !rango.desde)} onClick={() => setRango(r)}>{l}</button>)}
          <span style={{ color: C.ink4, margin: "0 4px" }}>o</span>
          <input type="date" value={desdeIn} max={hoyStr()} onChange={(e) => setDesdeIn(e.target.value)} style={input} aria-label="Desde" />
          <span style={{ color: C.ink3, fontSize: 13 }}>hasta</span>
          <input type="date" value={hastaIn} max={hoyStr()} onChange={(e) => setHastaIn(e.target.value)} style={input} aria-label="Hasta (opcional)" />
          <button style={pill(!!rango.desde)} onClick={aplicarFechas} disabled={!desdeIn}>Ver días</button>
          <button style={{ ...btn, marginLeft: "auto" }} onClick={refetch} disabled={loading}><RefreshCw size={14} strokeWidth={1.8} />{loading ? "Cargando…" : "Actualizar"}</button>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <Eyebrow style={{ width: 86 }}>Comparar</Eyebrow>
          {[["", "Nada"], ["previo", "Periodo anterior"], ["semana", "Hace una semana"]].map(([v, l]) => (
            <button key={v} style={pill(comparar === v)} onClick={() => setComparar(v)}>{l}</button>
          ))}
          {candidatos.length > 0 && (
            <select value={comparar.startsWith("eq:") ? comparar : ""} onChange={(e) => setComparar(e.target.value)} style={{ ...input, minWidth: 170 }}>
              <option value="">Con otro equipo…</option>
              {candidatos.map((x) => <option key={x.device} value={"eq:" + x.device}>{nombreDe(x.device)}</option>)}
            </select>
          )}
          {d && <span style={{ ...mono, fontSize: 11.5, color: C.ink3, marginLeft: "auto" }}>{rangoTxt}</span>}
        </div>
        {graficables.length > 0 && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
            <Eyebrow style={{ width: 86 }}>Medidas</Eyebrow>
            {graficables.map((x) => {
              const on = elegidos.includes(x.campo);
              return (
                <button key={x.campo} onClick={() => toggle(x.campo)}
                  style={{ ...pill(on), padding: "4px 11px", fontSize: 12.5, background: on ? C.light : "#fff", color: on ? C.primary : C.ink2, borderColor: on ? C.primary : C.line }}>
                  {etiqueta(x.campo, eq.tipo)[0]}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {error && !d && <div style={{ ...card, padding: 18, color: C.clay, marginBottom: 16 }}>No se ha podido cargar la ficha: {error}</div>}
      {!d && !error && <div style={{ ...card, padding: 24, color: C.ink3, marginBottom: 16 }}>Cargando…</div>}

      {d && (
        <>
          {d.mensajes === 0 && (
            <div style={{ ...card, padding: 18, marginBottom: 16, fontSize: 14, color: C.ink2, lineHeight: 1.6 }}>
              En este rango no hay lecturas guardadas de este equipo.{eq.ultimo ? ` La última es del ${fechaHora(eq.ultimo)}: prueba con un rango que la incluya.` : ""}
            </div>
          )}
          {grupos.map((g) => (
            <Tarjeta key={g.clave} style={{ marginBottom: 16 }}
              titulo={g.campos.map((k) => etiqueta(k, eq.tipo)[0]).join(" · ") + (g.unidad ? ` (${g.unidad})` : "")}
              extra={cmpInfo && <span style={{ fontSize: 12, color: C.ink3 }}>Línea discontinua: {cmpInfo.etiqueta.toLowerCase()}{cmpOk && !cmpOk.serie[g.campos[0]] ? " (sin datos)" : ""}</span>}>
              <GraficaCampos grupo={g} d={d} cmp={cmpOk} cmpInfo={cmpInfo} tipo={eq.tipo} isMobile={isMobile} />
            </Tarjeta>
          ))}
          {contadores.map((x) => <BarrasContador key={x.campo} k={x.campo} d={d} tipo={eq.tipo} />)}
          {d.estado_actual !== null && <Encendido d={d} />}

          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16, marginBottom: 16 }}>
            <Frecuencia d={d} eq={eq} />
            <Silencios d={d} />
          </div>
          <Tarjeta titulo="Actividad por horas" style={{ marginBottom: 16 }}>
            <MapaActividad actividad={d.actividad} limite={d.limite_min} />
          </Tarjeta>
          <Comparacion d={d} tipo={eq.tipo} />
          <PorDias d={d} tipo={eq.tipo} campo={elegidos[0] || (eq.principal && eq.principal.campo)} contador={contadores[0] && contadores[0].campo} />

          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16 }}>
            <Tarjeta titulo="Ajustes y valores que no cambian">
              {fijos.length === 0 && Object.keys(eq.textos || {}).length === 0
                ? <div style={{ fontSize: 13.5, color: C.ink3 }}>Nada que mostrar.</div>
                : (
                  <div style={{ fontSize: 13.5 }}>
                    {fijos.map((x) => {
                      const [l, u] = etiqueta(x.campo, eq.tipo);
                      return <FilaDato key={x.campo} l={l} v={x.bool ? (x.ultimo ? "sí" : "no") : fmt(x.ultimo, x.bool ? "" : u)} />;
                    })}
                    {Object.entries(eq.textos || {}).map(([k, v]) => <FilaDato key={k} l={etiqueta(k, eq.tipo)[0]} v={String(v)} />)}
                  </div>
                )}
            </Tarjeta>
            <Tarjeta titulo="Último mensaje recibido" extra={<button style={btn} onClick={descargar}><Download size={14} strokeWidth={1.8} />Descargar CSV</button>}>
              {d.ultimo ? (
                <>
                  <div style={{ fontSize: 13, color: C.ink3, marginBottom: 8 }}>{fechaHora(d.ultimo.ts)} · tal cual llega al servidor</div>
                  <pre style={{ ...mono, fontSize: 11.5, background: "#FAFAFA", border: `1px solid ${C.line}`, borderRadius: 10, padding: 12, maxHeight: 260, overflow: "auto", margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                    {JSON.stringify(d.ultimo.payload, null, 2)}
                  </pre>
                </>
              ) : <div style={{ fontSize: 13.5, color: C.ink3 }}>Todavía no ha llegado ningún mensaje.</div>}
            </Tarjeta>
          </div>
        </>
      )}
    </div>
  );
}

function FilaDato({ l, v }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "6px 0", borderTop: `1px solid ${C.soft}` }}>
      <span style={{ color: C.ink2 }}>{l}</span><span style={{ ...mono, color: C.ink, textAlign: "right", wordBreak: "break-word" }}>{v}</span>
    </div>
  );
}

// Ejes de tiempo con marcas en horas redondas
function ticksTiempo(t0, t1) {
  const span = t1 - t0;
  const ph = span <= 26 * HORA ? 3 : span <= 3.1 * DIA ? 12 : span <= 8 * DIA ? 24 : span <= 32 * DIA ? 96 : 168;
  const d = new Date(t0);
  d.setMinutes(0, 0, 0);
  if (ph >= 24) { d.setHours(0); if (d.getTime() < t0) d.setDate(d.getDate() + 1); }
  else d.setHours(Math.ceil(d.getHours() / ph) * ph);
  const out = [];
  while (d.getTime() <= t1 && out.length < 40) {
    if (d.getTime() >= t0) out.push(d.getTime());
    if (ph >= 24) d.setDate(d.getDate() + ph / 24); else d.setHours(d.getHours() + ph);
  }
  return out;
}
const fmtTick = (span) => (t) => {
  const d = new Date(t);
  if (span <= 26 * HORA) return `${pad(d.getHours())}:00`;
  if (span <= 3.1 * DIA) return d.getHours() === 0 ? `${DSEM[d.getDay()]} ${d.getDate()}` : `${pad(d.getHours())}:00`;
  return `${d.getDate()}/${d.getMonth() + 1}`;
};

// Junta series con instantes distintos: corta la línea en los silencios y rellena (interpola) los huecos cortos
function construirFilas(series, comp, thr, mult, banda) {
  const m = new Map();
  const fila = (t) => { let r = m.get(t); if (!r) { r = { t }; m.set(t, r); } return r; };
  const claves = [];
  series.forEach((s, i) => {
    claves.push(s.key);
    s.puntos.forEach(([t, a, mn, mx]) => { const r = fila(t); r[s.key] = a * mult; if (banda && i === 0) r.banda = [mn * mult, mx * mult]; });
  });
  if (comp) { claves.push("cmp"); comp.forEach(([t, a]) => { fila(t).cmp = a * mult; }); }
  for (const k of claves) {
    const pts = [...m.values()].filter((r) => esNum(r[k])).map((r) => r.t).sort((a, b) => a - b);
    for (let i = 1; i < pts.length; i++) {
      if (pts[i] - pts[i - 1] > thr) {
        const r = fila(Math.round((pts[i] + pts[i - 1]) / 2));
        r[k] = null;
        if (banda && k === claves[0]) r.banda = null;
      }
    }
  }
  const filas = [...m.values()].sort((a, b) => a.t - b.t);
  for (const k of [...claves, ...(banda ? ["banda"] : [])]) {
    let prev = -1;
    for (let i = 0; i < filas.length; i++) {
      const v = filas[i][k];
      if (v === undefined) continue;
      if (v === null) { prev = -1; continue; }
      if (prev >= 0 && i - prev > 1) {
        const a = filas[prev], b = filas[i];
        if (b.t - a.t <= thr) {
          for (let j = prev + 1; j < i; j++) {
            const f = (filas[j].t - a.t) / (b.t - a.t);
            filas[j][k] = k === "banda" ? [a[k][0] + f * (b[k][0] - a[k][0]), a[k][1] + f * (b[k][1] - a[k][1])] : a[k] + f * (b[k] - a[k]);
            filas[j]["_i_" + k] = true;
          }
        }
      }
      prev = i;
    }
  }
  return filas;
}

function GraficaCampos({ grupo, d, cmp, cmpInfo, tipo, isMobile }) {
  const t0 = Date.parse(d.desde), t1 = Date.parse(d.hasta), span = t1 - t0;
  const mult = grupo.bool ? 100 : 1;
  const thr = Math.max((d.limite_min || 60) * 60000, 3 * d.bucket_min * 60000);
  const banda = grupo.campos.length === 1;
  const series = grupo.campos.map((k, i) => ({ key: "s" + i, campo: k, puntos: d.serie[k] || [], color: colorDe(k, i), label: etiqueta(k, tipo)[0] }));
  const comp = cmp && cmpInfo && cmp.serie && cmp.serie[grupo.campos[0]]
    ? cmp.serie[grupo.campos[0]].map(([t, a, mn, mx]) => [t + cmpInfo.shift, a, mn, mx]).filter(([t]) => t >= t0 && t <= t1)
    : null;
  const filas = construirFilas(series, comp, thr, mult, banda);
  const refs = [];
  grupo.campos.forEach((k) => referencias(k, tipo).forEach((r) => { if (!refs.some((x) => x[0] === r[0])) refs.push(r); }));
  const ticks = ticksTiempo(t0, t1);
  const u = grupo.unidad;
  const Tip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const r = payload[0].payload;
    return (
      <div style={{ background: "#fff", border: `1px solid ${C.line}`, borderRadius: 10, padding: "8px 10px", fontSize: 12.5, boxShadow: "0 4px 14px rgba(0,0,0,0.06)" }}>
        <div style={{ ...mono, color: C.ink3, marginBottom: 4 }}>{fechaHora(r.t)}</div>
        {series.map((s) => esNum(r[s.key]) && (
          <div key={s.key} style={{ color: s.color }}>
            {s.label}: <b>{fmt(r[s.key], u)}</b>{r["_i_" + s.key] ? " (aprox.)" : ""}
            {banda && r.banda && !r._i_banda && r.banda[0] !== r.banda[1] ? <span style={{ color: C.ink3 }}> · de {fmt(r.banda[0], u)} a {fmt(r.banda[1], u)}</span> : null}
          </div>
        ))}
        {esNum(r.cmp) && <div style={{ color: C.ink3 }}>{cmpInfo.etiqueta}{cmpInfo.shift ? ` (${fechaHora(r.t - cmpInfo.shift)})` : ""}: <b>{fmt(r.cmp, u)}</b></div>}
      </div>
    );
  };
  if (!filas.length) return <div style={{ fontSize: 13.5, color: C.ink3 }}>Sin lecturas en este rango.</div>;
  return (
    <div>
      <div style={{ width: "100%", height: isMobile ? 200 : 240 }}>
        <ResponsiveContainer>
          <ComposedChart data={filas} margin={{ top: 6, right: 12, bottom: 0, left: 0 }}>
            <CartesianGrid stroke={C.soft} vertical={false} />
            <XAxis dataKey="t" type="number" domain={[t0, t1]} ticks={ticks} tickFormatter={fmtTick(span)} tick={{ fontSize: 11, fill: C.ink3 }} stroke={C.line} allowDataOverflow />
            <YAxis width={54} tick={{ fontSize: 11, fill: C.ink3 }} stroke={C.line} tickFormatter={(v) => nf(v, decimales(u))}
              domain={grupo.bool ? [0, 100] : ["auto", "auto"]} />
            <Tooltip content={<Tip />} />
            {refs.map(([y, l]) => <ReferenceLine key={y} y={y} stroke={C.clay} strokeDasharray="4 4" strokeOpacity={0.6} label={{ value: l, position: "insideTopRight", fontSize: 10, fill: C.clay }} />)}
            {banda && <Area dataKey="banda" stroke="none" fill={series[0].color} fillOpacity={0.14} isAnimationActive={false} connectNulls={false} />}
            {series.map((s) => (
              <Line key={s.key} dataKey={s.key} stroke={s.color} strokeWidth={1.8} dot={false} isAnimationActive={false} connectNulls={false}
                type={grupo.bool ? "stepAfter" : "linear"} />
            ))}
            {comp && <Line dataKey="cmp" stroke={C.ink3} strokeWidth={1.4} strokeDasharray="5 4" dot={false} isAnimationActive={false} connectNulls={false} />}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 8, fontSize: 12.5, color: C.ink2 }}>
        {series.map((s) => {
          const c = d.campos.find((x) => x.campo === s.campo) || {};
          return (
            <span key={s.key} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 12, height: 3, background: s.color, display: "inline-block", borderRadius: 2 }} />
              {s.label}: ahora <b>{fmt(c.ultimo * mult, u)}</b> · media {fmt(c.media * mult, u)} · mín. {fmt(c.min * mult, u)} · máx. {fmt(c.max * mult, u)}
            </span>
          );
        })}
        {banda && <span style={{ color: C.ink3 }}>La franja clara es el mínimo y el máximo de cada tramo.</span>}
      </div>
    </div>
  );
}

function BarrasContador({ k, d, tipo }) {
  const [label, u] = etiquetaInc(k, tipo);
  const span = Date.parse(d.hasta) - Date.parse(d.desde);
  const ph = d.por_hora && d.por_hora[k];
  const porHora = ph && span <= 3.1 * DIA;
  const datos = porHora
    ? ph.map(([t, v]) => ({ etq: `${diaCorto(new Date(t).toISOString().slice(0, 10)).split(" ")[0]} ${pad(new Date(t).getHours())}:00`, v, t }))
    : d.diario.map((x) => ({ etq: diaCorto(x.dia), v: x.campos[k] ? x.campos[k].total || 0 : 0 }));
  const c = d.campos.find((x) => x.campo === k) || {};
  const prev = c.previo && c.previo.total;
  return (
    <Tarjeta style={{ marginBottom: 16 }} titulo={`${label} ${porHora ? "por hora" : "por día"}${u ? ` (${u})` : ""}`}
      extra={<span style={{ fontSize: 12.5, color: C.ink2 }}>Total: <b>{fmt(c.total, u)}</b>{esNum(prev) ? <> · periodo anterior {fmt(prev, u)}</> : null}</span>}>
      <div style={{ width: "100%", height: 200 }}>
        <ResponsiveContainer>
          <BarChart data={datos} margin={{ top: 6, right: 12, bottom: 0, left: 0 }}>
            <CartesianGrid stroke={C.soft} vertical={false} />
            <XAxis dataKey="etq" tick={{ fontSize: 11, fill: C.ink3 }} stroke={C.line} minTickGap={18} />
            <YAxis width={54} tick={{ fontSize: 11, fill: C.ink3 }} stroke={C.line} tickFormatter={(v) => nf(v, decimales(u))} />
            <Tooltip formatter={(v) => [fmt(v, u), label]} labelStyle={{ color: C.ink3 }} />
            <Bar dataKey="v" fill={C.sky} radius={[3, 3, 0, 0]} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Tarjeta>
  );
}

function Encendido({ d }) {
  const datos = d.diario.map((x) => ({ etq: diaCorto(x.dia), h: esNum(x.on_min) ? Math.round((x.on_min / 60) * 10) / 10 : 0 }));
  const cambios = d.cambios.filter((c) => c.campo === "state");
  if (!cambios.length && datos.every((x) => !x.h)) {
    return (
      <Tarjeta style={{ marginBottom: 16 }} titulo="Encendido y apagado">
        <div style={{ fontSize: 14, color: C.ink2 }}>
          {d.estado_actual === "OFF" ? "Ha estado apagado todo el rango, sin cambios." : `Estado: ${d.estado_actual}. No ha cambiado en este rango.`}
        </div>
      </Tarjeta>
    );
  }
  return (
    <Tarjeta style={{ marginBottom: 16 }} titulo="Encendido y apagado"
      extra={<span style={{ fontSize: 12.5, color: C.ink2 }}>Ahora: <b>{d.estado_actual === "ON" ? "encendido" : d.estado_actual === "OFF" ? "apagado" : d.estado_actual}</b> · {d.cambios.filter((c) => c.campo === "state").length} cambios en el rango</span>}>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.4fr) minmax(0, 1fr)", gap: 16 }}>
        <div style={{ width: "100%", height: 170 }}>
          <ResponsiveContainer>
            <BarChart data={datos} margin={{ top: 6, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid stroke={C.soft} vertical={false} />
              <XAxis dataKey="etq" tick={{ fontSize: 11, fill: C.ink3 }} stroke={C.line} minTickGap={12} />
              <YAxis width={36} tick={{ fontSize: 11, fill: C.ink3 }} stroke={C.line} />
              <Tooltip formatter={(v) => [`${nf(v, 1)} h`, "Encendido"]} />
              <Bar dataKey="h" fill={C.ochre} radius={[3, 3, 0, 0]} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div style={{ fontSize: 13, maxHeight: 170, overflow: "auto" }}>
          {d.cambios.filter((c) => c.campo === "state").slice(-12).reverse().map((c, i) => (
            <FilaDato key={i} l={fechaHora(c.t)} v={`${c.antes} → ${c.valor}`} />
          ))}
          {!d.cambios.some((c) => c.campo === "state") && <div style={{ color: C.ink3 }}>No ha cambiado de estado en este rango.</div>}
        </div>
      </div>
    </Tarjeta>
  );
}

function Frecuencia({ d, eq }) {
  const iv = d.intervalos;
  const total = iv.hist.reduce((s, h) => s + h.n, 0);
  return (
    <Tarjeta titulo="Cada cuánto envía">
      <div style={{ fontSize: 26, fontWeight: 700, color: C.ink, fontFamily: "var(--font-serif)" }}>{cada(iv.medio_s)}</div>
      <div style={{ fontSize: 12.5, color: C.ink3, marginBottom: 12 }}>de media, sin contar los silencios</div>
      {iv.hist.map((h) => (
        <div key={h.etiqueta} className="er-diag-hist" style={{ display: "grid", gridTemplateColumns: "110px 1fr 44px", gap: 8, alignItems: "center", fontSize: 12.5, color: C.ink2, marginBottom: 4 }}>
          <span>{h.etiqueta}</span>
          <span style={{ height: 8, background: C.soft, borderRadius: 4, overflow: "hidden" }}>
            <span style={{ display: "block", height: "100%", width: total ? `${(100 * h.n) / total}%` : 0, background: C.sky }} />
          </span>
          <span style={{ ...mono, textAlign: "right" }}>{h.n}</span>
        </div>
      ))}
      <div style={{ fontSize: 12.5, color: C.ink3, marginTop: 10, lineHeight: 1.55 }}>
        {esNum(iv.p90_s) && <>9 de cada 10 lecturas llegan en menos de {cada(iv.p90_s).replace("cada ", "")}. </>}
        {d.guardado_min ? <>Se guarda como mucho una lectura cada {d.guardado_min} min aunque el equipo envíe más a menudo. </> : null}
        {eq.memoria && eq.memoria.recibidos > 1 && (
          <>Desde el último reinicio del servidor ({fechaHora(eq.memoria.desde)}) han llegado {nf(eq.memoria.recibidos, 0)} mensajes en total ({cada((Date.now() - Date.parse(eq.memoria.desde)) / 1000 / (eq.memoria.recibidos - 1))}).</>
        )}
      </div>
    </Tarjeta>
  );
}

function Silencios({ d }) {
  return (
    <Tarjeta titulo="Silencios más largos" extra={<span style={{ fontSize: 12.5, color: C.ink2 }}>Sin datos en total: <b>{durMin(d.silencio_total_min)}</b></span>}>
      <div style={{ fontSize: 12.5, color: C.ink3, marginBottom: 8 }}>Ratos de más de {durMin(d.limite_min)} sin recibir nada.</div>
      {d.silencios.length === 0
        ? <div style={{ fontSize: 14, color: C.moss, fontWeight: 600 }}>Ninguno en este rango.</div>
        : d.silencios.map((s, i) => (
          <FilaDato key={i} l={`${fechaHora(s.desde)} → ${s.hasta ? fechaHora(s.hasta) : "ahora"}`} v={durMin(s.min)} />
        ))}
    </Tarjeta>
  );
}

function MapaActividad({ actividad, limite }) {
  const filas = actividad.slice(-31);
  const max = Math.max(1, ...filas.flatMap((f) => f.horas));
  const ahora = Date.now();
  // antes del primer mensaje del rango el equipo puede que ni existiera: gris, no rojizo
  let primero = null;
  filas.some((f) => f.horas.some((n, h) => { if (n) { primero = new Date(f.dia + "T00:00:00").getTime() + h * HORA; return true; } return false; }));
  return (
    <div style={{ overflowX: "auto" }}>
      <div className="er-diag-heat" style={{ display: "grid", gridTemplateColumns: "70px repeat(24, minmax(9px, 1fr))", gap: 2, minWidth: 340 }}>
        <div />
        {Array.from({ length: 24 }, (_, h) => <div key={h} style={{ ...mono, fontSize: 9.5, color: C.ink4, textAlign: "center" }}>{h % 3 === 0 ? h : ""}</div>)}
        {filas.map((f) => (
          <React.Fragment key={f.dia}>
            <div style={{ fontSize: 11.5, color: C.ink3, whiteSpace: "nowrap", lineHeight: "14px" }}>{diaCorto(f.dia)}</div>
            {f.horas.map((n, h) => {
              const t = new Date(f.dia + "T00:00:00"); t.setHours(h);
              const ts = t.getTime();
              const futuro = ts > ahora;
              const bg = futuro ? "transparent" : n ? verde(n / max) : limite <= 60 && primero !== null && ts >= primero ? "#EBC9BA" : C.soft;
              return <div key={h} title={`${diaCorto(f.dia)} · ${pad(h)}:00 · ${n} lectura${n === 1 ? "" : "s"}`}
                style={{ height: 14, borderRadius: 2, background: bg, border: futuro ? `1px dashed ${C.soft}` : "none", boxSizing: "border-box" }} />;
            })}
          </React.Fragment>
        ))}
      </div>
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 10, fontSize: 12, color: C.ink3, alignItems: "center" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 12, height: 10, background: verde(0.2), borderRadius: 2 }} />pocas lecturas</span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 12, height: 10, background: verde(1), borderRadius: 2 }} />muchas</span>
        {limite <= 60 && <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 12, height: 10, background: "#EBC9BA", borderRadius: 2 }} />ninguna (debería)</span>}
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 12, height: 10, background: C.soft, borderRadius: 2 }} />ninguna</span>
        {actividad.length > 31 && <span>Se muestran los últimos 31 días.</span>}
      </div>
    </div>
  );
}

function Comparacion({ d, tipo }) {
  const ABS = ["°C", "%", "V", "Hz", "", "LQI", "m", "mV"];
  const filas = d.campos.filter((x) => (x.variable || (x.contador && ((x.total || 0) > 0 || (x.previo && x.previo.total > 0)))) && x.campo !== "linkquality").slice(0, 14);
  const cambio = (a, b, u, cont) => {
    if (!esNum(a) || !esNum(b)) return "—";
    if (!cont && ABS.includes(u)) { const df = a - b; return `${df >= 0 ? "+" : ""}${nf(df, decimales(u))}${u ? " " + u : ""}`; }
    if (b === 0) return a === 0 ? "igual" : "—";
    const p = ((a - b) / Math.abs(b)) * 100;
    return `${p >= 0 ? "+" : ""}${nf(p, 0)} %`;
  };
  const p = d.previo;
  return (
    <Tarjeta style={{ marginBottom: 16 }} titulo="Comparado con el periodo anterior"
      extra={<span style={{ ...mono, fontSize: 11.5, color: C.ink3 }}>antes: {fechaHora(p.desde)} → {fechaHora(p.hasta)}</span>}>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 520 }}>
          <thead>
            <tr>{["Medida", "Ahora", "Antes", "Cambio"].map((h) => <th key={h} style={{ ...mono, fontSize: 10, letterSpacing: 0.9, textTransform: "uppercase", color: C.ink3, textAlign: h === "Medida" ? "left" : "right", padding: "6px 8px", fontWeight: 500 }}>{h}</th>)}</tr>
          </thead>
          <tbody>
            <tr style={{ borderTop: `1px solid ${C.soft}` }}>
              <td style={{ padding: "7px 8px", color: C.ink2 }}>Lecturas guardadas</td>
              <td style={{ padding: "7px 8px", textAlign: "right", fontWeight: 600 }}>{nf(d.mensajes, 0)}</td>
              <td style={{ padding: "7px 8px", textAlign: "right" }}>{nf(p.mensajes, 0)}</td>
              <td style={{ padding: "7px 8px", textAlign: "right" }}>{cambio(d.mensajes, p.mensajes, "", true)}</td>
            </tr>
            {filas.map((x) => {
              const [l, u] = x.contador ? etiquetaInc(x.campo, tipo) : etiqueta(x.campo, tipo);
              const m = x.bool ? 100 : 1, uu = x.bool ? "%" : u;
              const ahora = x.contador ? x.total : x.media * m;
              const antes = x.previo ? (x.contador ? x.previo.total : x.previo.media * m) : null;
              return (
                <tr key={x.campo} style={{ borderTop: `1px solid ${C.soft}` }}>
                  <td style={{ padding: "7px 8px", color: C.ink2 }}>{l}{x.contador ? " (total)" : " (media)"}</td>
                  <td style={{ padding: "7px 8px", textAlign: "right", fontWeight: 600 }}>
                    {fmt(ahora, uu)}{!x.contador && <div style={{ fontSize: 11.5, color: C.ink3, fontWeight: 400 }}>{fmt(x.min * m, uu)} a {fmt(x.max * m, uu)}</div>}
                  </td>
                  <td style={{ padding: "7px 8px", textAlign: "right" }}>
                    {x.previo ? fmt(antes, uu) : "—"}{!x.contador && x.previo && <div style={{ fontSize: 11.5, color: C.ink3 }}>{fmt(x.previo.min * m, uu)} a {fmt(x.previo.max * m, uu)}</div>}
                  </td>
                  <td style={{ padding: "7px 8px", textAlign: "right", color: C.ink2 }}>{cambio(ahora, antes, uu, x.contador)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Tarjeta>
  );
}

function PorDias({ d, tipo, campo, contador }) {
  const [l, u] = campo ? etiqueta(campo, tipo) : ["", ""];
  const c = d.campos.find((x) => x.campo === campo);
  const m = c && c.bool ? 100 : 1, uu = c && c.bool ? "%" : u;
  const [li, ui] = contador ? etiquetaInc(contador, tipo) : ["", ""];
  const hayOn = d.diario.some((x) => esNum(x.on_min));
  const filas = [...d.diario].reverse();
  const th = (h, left) => <th key={h} style={{ ...mono, fontSize: 10, letterSpacing: 0.9, textTransform: "uppercase", color: C.ink3, textAlign: left ? "left" : "right", padding: "6px 8px", fontWeight: 500 }}>{h}</th>;
  return (
    <Tarjeta style={{ marginBottom: 16 }} titulo="Día a día">
      <div style={{ overflowX: "auto", maxHeight: 360, overflowY: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 460 }}>
          <thead>
            <tr>
              {th("Día", true)}{th("Lecturas")}
              {campo && [th(`${l} mín.`), th("media"), th("máx.")]}
              {contador && th(`${li}${ui ? ` (${ui})` : ""}`)}
              {hayOn && th("Encendido")}
            </tr>
          </thead>
          <tbody>
            {filas.map((x) => {
              const v = campo && x.campos[campo];
              return (
                <tr key={x.dia} style={{ borderTop: `1px solid ${C.soft}`, background: x.n === 0 ? "#FBF6F3" : "transparent" }}>
                  <td style={{ padding: "7px 8px", color: C.ink2, whiteSpace: "nowrap" }}>{diaCorto(x.dia)}</td>
                  <td style={{ padding: "7px 8px", textAlign: "right", color: x.n ? C.ink : C.clay, fontWeight: 600 }}>{x.n}</td>
                  {campo && [
                    <td key="a" style={{ padding: "7px 8px", textAlign: "right" }}>{v ? fmt(v.min * m, uu) : "—"}</td>,
                    <td key="b" style={{ padding: "7px 8px", textAlign: "right", fontWeight: 600 }}>{v ? fmt(v.media * m, uu) : "—"}</td>,
                    <td key="c" style={{ padding: "7px 8px", textAlign: "right" }}>{v ? fmt(v.max * m, uu) : "—"}</td>,
                  ]}
                  {contador && <td style={{ padding: "7px 8px", textAlign: "right" }}>{x.campos[contador] ? fmt(x.campos[contador].total, "") : "—"}</td>}
                  {hayOn && <td style={{ padding: "7px 8px", textAlign: "right" }}>{esNum(x.on_min) ? durMin(x.on_min) : "—"}</td>}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Tarjeta>
  );
}
